import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { 
  Download, 
  TrendingUp, 
  Package,
  Users,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Database,
  Printer
} from 'lucide-react';
import { TakaIcon, DoubleScrollbar } from '../../components/UI';
import { apiService } from '../../services/apiService';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import toast from 'react-hot-toast';
import Modal from '../../components/UI/Modal';
import PrintModal from '../../components/UI/PrintModal';
import DeliveryInvoicePrintView from '../Invoices/DeliveryInvoicePrintView';
import ReactDOM from 'react-dom';
import { useSettings } from '../../contexts/SettingsContext';
import { formatDateTime } from '../../utils/formatDateTime';
import Button from '../../components/UI/Button';

interface ReportData {
  salesReport: {
    totalSales: number;
    totalInvoices: number;
    averageOrderValue: number;
    topProducts: Array<{
      name: string;
      quantity: number;
      revenue: number;
    }>;
    monthlyData: Array<{
      month: string;
      sales: number;
      invoices: number;
    }>;
  };
  inventoryReport: {
    totalProducts: number;
    lowStockProducts: number;
    outOfStockProducts: number;
    totalValue: number;
    topCategories: Array<{
      name: string;
      productCount: number;
      totalValue: number;
      lowStockCount: number;
      outOfStockCount: number;
    }>;
  };
  projectReport: {
    totalProjects: number;
    activeProjects: number;
    completedProjects: number;
    totalBudget: number;
    totalRevenue: number;
    projectStatus: Array<{
      status: string;
      count: number;
    }>;
  };
  productStockReport?: {
    products: Array<{
      _id: string;
      name: string;
      code: string;
      description: string;
      category: string;
      parentCategory: string;
      subCategory: string;
      brand: string;
      location: string;
      unit: string;
      productType: string;
      compressorType: string;
      country: string;
      currentStock: number;
      minStockLevel: number;
      price: number;
      totalValue: number;
      model: string;
      status: string;
      createdAt: string;
      updatedAt: string;
    }>;
    categories: Array<{
      _id: string;
      name: string;
    }>;
    summary: {
      totalProducts: number;
      totalStockValue: number;
      lowStockProducts: number;
      outOfStockProducts: number;
      inStockProducts: number;
    };
    categorySummary: Array<{
      _id: string;
      categoryName: string;
      productCount: number;
      totalStock: number;
      totalValue: number;
      lowStockCount: number;
      outOfStockCount: number;
    }>;
  };
}

// PrintPortal component for print overlays
const PrintPortal: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const printRoot = document.getElementById('print-root');
  return printRoot ? ReactDOM.createPortal(children, printRoot) : null;
};

// Styled print view for Sales Report
const SalesReportPrintView = ({ data, currencySymbol }: { data: any, currencySymbol: string }) => {
  const sales = data?.salesReport || {};
  return (
    <div className="font-[var(--font-family)] text-[var(--color-text)] p-6">
      <h1 className="text-2xl font-bold mb-4">Sales Report</h1>
      <div className="flex gap-8 mb-6">
        <div><strong>Total Sales:</strong> {currencySymbol}{sales.totalSales?.toLocaleString?.() ?? 0}</div>
        <div><strong>Total Invoices:</strong> {sales.totalInvoices ?? 0}</div>
        <div><strong>Average Order Value:</strong> {currencySymbol}{sales.averageOrderValue?.toLocaleString?.() ?? 0}</div>
      </div>
      <h2 className="text-xl font-bold my-6">Top Products</h2>
      <table className="w-full border-collapse mb-6">
        <thead>
          <tr className="bg-gray-100">
            <th className="border border-gray-300 p-2 text-left">Name</th>
            <th className="border border-gray-300 p-2">Quantity</th>
            <th className="border border-gray-300 p-2">Revenue ({currencySymbol})</th>
          </tr>
        </thead>
        <tbody>
          {(sales.topProducts || []).map((prod: any) => (
            <tr key={prod.name}>
              <td className="border border-gray-300 p-2">{prod.name}</td>
              <td className="border border-gray-300 p-2">{prod.quantity}</td>
              <td className="border border-gray-300 p-2">{prod.revenue}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <h2 className="text-xl font-bold my-6">Monthly Data</h2>
      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-gray-100">
            <th className="border border-gray-300 p-2">Month</th>
            <th className="border border-gray-300 p-2">Sales ({currencySymbol})</th>
            <th className="border border-gray-300 p-2">Invoices</th>
          </tr>
        </thead>
        <tbody>
          {(sales.monthlyData || []).map((row: any, i: number) => (
            <tr key={i}>
              <td className="border border-gray-300 p-2">{row.month}</td>
              <td className="border border-gray-300 p-2">{row.sales?.toLocaleString?.() ?? 0}</td>
              <td className="border border-gray-300 p-2">{row.invoices}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const InventoryReportPrintView = ({ data, currencySymbol }: { data: any, currencySymbol: string }) => {
  const inv = data?.inventoryReport || {};
  return (
    <div className="font-['Times_New_Roman'] text-[var(--color-text)] p-6">
      <h1 className="text-2xl font-bold mb-4">Inventory Report</h1>
      <div className="flex gap-8 mb-6">
        <div><strong>Total Products:</strong> {inv.totalProducts ?? 0}</div>
        <div><strong>Low Stock:</strong> {inv.lowStockProducts ?? 0}</div>
        <div><strong>Out of Stock:</strong> {inv.outOfStockProducts ?? 0}</div>
        <div><strong>Total Value:</strong> {currencySymbol}{inv.totalValue?.toLocaleString?.() ?? 0}</div>
      </div>
      <h2 className="text-xl font-bold my-6">Top Parent Categories</h2>
      <table className="w-full border-collapse mb-6">
        <thead>
          <tr className="bg-gray-100">
            <th className="border border-gray-300 p-2">Parent Category</th>
            <th className="border border-gray-300 p-2">Product Count</th>
            <th className="border border-gray-300 p-2">Low Stock</th>
            <th className="border border-gray-300 p-2">Out of Stock</th>
            <th className="border border-gray-300 p-2">Total Value ({currencySymbol})</th>
          </tr>
        </thead>
        <tbody>
          {(inv.topCategories || []).map((cat: any) => (
            <tr key={cat.name}>
              <td className="border border-gray-300 p-2">{cat.name}</td>
              <td className="border border-gray-300 p-2 text-center">{cat.productCount}</td>
              <td className="border border-gray-300 p-2 text-center text-yellow-600">{cat.lowStockCount || 0}</td>
              <td className="border border-gray-300 p-2 text-center text-red-600">{cat.outOfStockCount || 0}</td>
              <td className="border border-gray-300 p-2 text-right">{cat.totalValue?.toLocaleString?.() ?? 0}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const ProductStockReportPrintView = ({ data, currencySymbol }: { data: any, currencySymbol: string }) => {
  const stock = data?.productStockReport || {};
  return (
    <div className="font-['Times_New_Roman'] text-[var(--color-text)] p-6">
      <h1 className="text-2xl font-bold mb-4">Product Stock Report</h1>
      <div className="flex gap-8 mb-6">
        <div><strong>Total Products:</strong> {stock.summary?.totalProducts ?? 0}</div>
        <div><strong>Total Stock Value:</strong> {currencySymbol}{stock.summary?.totalStockValue?.toLocaleString?.() ?? 0}</div>
        <div><strong>Low Stock:</strong> {stock.summary?.lowStockProducts ?? 0}</div>
        <div><strong>Out of Stock:</strong> {stock.summary?.outOfStockProducts ?? 0}</div>
      </div>
      <h2 className="text-xl font-bold my-6">Products</h2>
      <table className="w-full border-collapse text-sm mb-6 table-fixed">
        <thead>
          <tr className="bg-gray-100">
            <th className="border border-gray-300 p-2 align-top w-[4.5rem]">Name</th>
            <th className="border border-gray-300 p-2">Category</th>
            <th className="border border-gray-300 p-2">Brand</th>
            <th className="border border-gray-300 p-2">Location</th>
            <th className="border border-gray-300 p-2">Unit</th>
            <th className="border border-gray-300 p-2">Stock</th>
            <th className="border border-gray-300 p-2">Min Stock</th>
            <th className="border border-gray-300 p-2">Price ({currencySymbol})</th>
            <th className="border border-gray-300 p-2">Total Value ({currencySymbol})</th>
          </tr>
        </thead>
        <tbody>
          {(stock.products || []).map((prod: any) => (
            <tr key={prod._id}>
              <td className="border border-gray-300 p-2 whitespace-normal break-words break-all align-top max-w-[4.5rem]">{prod.name}</td>
              <td className="border border-gray-300 p-2">{prod.category}</td>
              <td className="border border-gray-300 p-2">{prod.brand}</td>
              <td className="border border-gray-300 p-2">{prod.location}</td>
              <td className="border border-gray-300 p-2">{prod.unit}</td>
              <td className="border border-gray-300 p-2 text-center">{prod.currentStock}</td>
              <td className="border border-gray-300 p-2 text-center">{prod.minStockLevel}</td>
              <td className="border border-gray-300 p-2 text-right">{prod.price?.toLocaleString?.() ?? 0}</td>
              <td className="border border-gray-300 p-2 text-right">{prod.totalValue?.toLocaleString?.() ?? 0}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const SupplierPurchaseReportPrintView = ({ data, currencySymbol }: { data: any, currencySymbol: string }) => {
  const suppliers = data?.suppliers || [];
  return (
    <div className="font-['Times_New_Roman'] text-[var(--color-text)] p-6">
      <h1 className="text-2xl font-bold mb-4">Supplier Product Purchase Report</h1>
      {suppliers.map((sup: any) => {
        let rows;
        if (Array.isArray(sup.products)) {
          rows = sup.products.map((prod: any) => (
            <tr key={prod.productName}>
              <td className="border border-gray-300 p-2">{prod.productName}</td>
              <td className="border border-gray-300 p-2 text-center">{prod.totalQty}</td>
              <td className="border border-gray-300 p-2">{prod.unit}</td>
            </tr>
          ));
        } else {
          rows = Object.entries(sup.products || {}).map(([productName, prod]: [string, any]) => (
            <tr key={productName}>
              <td className="border border-gray-300 p-2">{productName}</td>
              <td className="border border-gray-300 p-2 text-center">{prod.qty}</td>
              <td className="border border-gray-300 p-2">{prod.unit}</td>
            </tr>
          ));
        }
        return (
          <div key={sup._id} className="mb-8">
            <table className="w-full border-collapse text-sm mb-6">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 p-2">Product</th>
                  <th className="border border-gray-300 p-2">Total Qty</th>
                  <th className="border border-gray-300 p-2">Unit</th>
                </tr>
              </thead>
              <tbody>{rows}</tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
};

const SupplierInvoiceReportPrintView = ({ data, currencySymbol }: { data: any, currencySymbol: string }) => {
  const suppliers = data?.suppliers || [];
  return (
    <div className="font-['Times_New_Roman'] text-[var(--color-text)] p-6">
      <h1 className="text-2xl font-bold mb-4">Supplier Purchase Invoice Report</h1>
      {suppliers.map((sup: any) => (
        <div key={sup._id} className="mb-8">
          <h2 className="text-xl font-bold my-4">{sup.name}</h2>
          {(sup.invoices && sup.invoices.length > 0) ? (
            sup.invoices.map((inv: any, invIdx: number) => (
              <div key={inv._id || invIdx} className="mb-6">
                <div className="font-bold text-base text-blue-600 mb-2">Invoice #: {inv.invoiceNumber}</div>
                <div className="text-sm text-gray-700 mb-2">Date: {inv.createdAt ? new Date(inv.createdAt).toLocaleDateString() : '-'}</div>
                <div className="text-sm text-gray-700 mb-2">Status: <span className="font-semibold">{inv.status}</span></div>
                <table className="w-full border-collapse mt-2 text-sm">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-gray-300 p-2">Product Name</th>
                      <th className="border border-gray-300 p-2">Unit</th>
                      <th className="border border-gray-300 p-2">Qty</th>
                      <th className="border border-gray-300 p-2">Rate</th>
                      <th className="border border-gray-300 p-2">Amount</th>
                      <th className="border border-gray-300 p-2">Discount</th>
                      <th className="border border-gray-300 p-2">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(inv.items || []).map((item: any, itemIdx: number) => (
                      <tr key={itemIdx}>
                        <td className="border border-gray-300 p-2">{item.productName || item.product?.name || '-'}</td>
                        <td className="border border-gray-300 p-2">{item.unit || item.product?.unit || '-'}</td>
                        <td className="border border-gray-300 p-2 text-right">{item.qty ?? item.quantity ?? 0}</td>
                        <td className="border border-gray-300 p-2 text-right">{item.purchaseRate ?? item.unitPrice ?? 0}</td>
                        <td className="border border-gray-300 p-2 text-right">{typeof item.amount !== 'undefined' && item.amount !== null && item.amount !== '-' ? item.amount : (() => { const qty = parseFloat(item.qty ?? item.quantity ?? 0); const rate = parseFloat(item.purchaseRate ?? item.unitPrice ?? 0); if (!isNaN(qty) && !isNaN(rate)) { const calcAmount = qty * rate; return isNaN(calcAmount) ? 0 : calcAmount; } return 0; })()}</td>
                        <td className="border border-gray-300 p-2">{item.discount ?? 0}</td>
                        <td className="border border-gray-300 p-2 text-right">{typeof item.total !== 'undefined' && item.total !== null && item.total !== '-' ? item.total : (() => { const qty = parseFloat(item.qty ?? item.quantity ?? 0); const rate = parseFloat(item.purchaseRate ?? item.unitPrice ?? 0); const discount = parseFloat(item.discount ?? 0); if (!isNaN(qty) && !isNaN(rate)) { const calcTotal = qty * rate - discount; return isNaN(calcTotal) ? '-' : calcTotal.toLocaleString(); } return '-'; })()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="text-right mt-2 font-bold text-base">Total: {currencySymbol}{(inv.total || 0).toLocaleString()}</div>
              </div>
            ))
          ) : (
            <div className="text-gray-500 italic my-4">No purchase invoices found for this supplier or period.</div>
          )}
        </div>
      ))}
    </div>
  );
};

const ProjectInvoiceReportPrintView = ({ data, currencySymbol }: { data: any, currencySymbol: string }) => {
  const projects = data?.projects || [];
  return (
    <div className="font-['Times_New_Roman'] text-[var(--color-text)] p-6">
      <h1 className="text-2xl font-bold mb-4">Project Delivery Invoice Report</h1>
      {projects.map((proj: any) => (
        <div key={proj._id} className="mb-8">
          <h2 className="text-xl font-bold my-4">{proj.name}</h2>
          <table className="w-full border-collapse text-sm mb-6">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 p-2">Invoice Number</th>
                <th className="border border-gray-300 p-2">Date</th>
                <th className="border border-gray-300 p-2">Customer</th>
                <th className="border border-gray-300 p-2">Total ({currencySymbol})</th>
              </tr>
            </thead>
            <tbody>
              {(proj.invoices || []).map((inv: any) => (
                <tr key={inv._id}>
                  <td className="border border-gray-300 p-2">{inv.invoiceNumber}</td>
                  <td className="border border-gray-300 p-2">{inv.date}</td>
                  <td className="border border-gray-300 p-2">{typeof inv.customer === 'object' ? inv.customer?.name || '-' : inv.customer || '-'}</td>
                  <td className="border border-gray-300 p-2 text-right">{currencySymbol}{inv.total?.toLocaleString?.() ?? 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
};

const ProjectProductReportPrintView = ({ data, currencySymbol }: { data: any, currencySymbol: string }) => {
  const products = data?.products || [];
  return (
    <div className="font-['Times_New_Roman'] text-[var(--color-text)] p-6">
      <h1 className="text-2xl font-bold mb-4">Project Product Delivery Report</h1>
      <table className="min-w-full border-collapse text-sm">
        <thead>
          <tr className="bg-gray-100">
            <th className="border border-gray-300 p-2">Product</th>
            <th className="border border-gray-300 p-2">Unit</th>
            <th className="border border-gray-300 p-2">Total Qty</th>
            <th className="border border-gray-300 p-2">Total Sales ({currencySymbol})</th>
          </tr>
        </thead>
        <tbody>
          {(products || []).map((prod: any) => (
            <tr key={prod.productName || '-'}>
              <td className="border border-gray-300 p-2">{prod.productName || '-'}</td>
              <td className="border border-gray-300 p-2">{prod.unit || '-'}</td>
              <td className="border border-gray-300 p-2 text-center">{prod.qty ?? 0}</td>
              <td className="border border-gray-300 p-2 text-right">{currencySymbol}{prod.total?.toLocaleString?.() ?? 0}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// 1. Add DetailsModalPrintView component after ProductStockReportPrintView
const DetailsModalPrintView = ({ data, currencySymbol }: { data: any, currencySymbol: string }) => {
  if (!data) return null;
  return (
    <div className="font-['Times_New_Roman'] text-[var(--color-text)] p-6">
      <h1 className="text-xl font-bold mb-4 text-center">{data.productName} - {data.entries[0]?.customer || '-'} / {data.projectName || '-'} Details</h1>
      <table className="min-w-full border-collapse text-sm">
        <thead>
          <tr className="bg-gray-100">
            <th className="border border-gray-300 p-2">Invoice #</th>
            <th className="border border-gray-300 p-2">Date</th>
            <th className="border border-gray-300 p-2">Qty</th>
            <th className="border border-gray-300 p-2">Unit</th>
            <th className="border border-gray-300 p-2">Rate</th>
            <th className="border border-gray-300 p-2">Amount</th>
            <th className="border border-gray-300 p-2">Discount</th>
            <th className="border border-gray-300 p-2">Total</th>
          </tr>
        </thead>
        <tbody>
          {data.entries.map((entry: any, idx: number) => (
            <tr key={idx}>
              <td className="border border-gray-300 p-2">{entry.invoiceNumber}</td>
              <td className="border border-gray-300 p-2">{entry.date ? new Date(entry.date).toISOString().slice(0, 10) : '-'}</td>
              <td className="border border-gray-300 p-2 text-right">{entry.qty ?? entry.quantity ?? 0}</td>
              <td className="border border-gray-300 p-2">{entry.unit}</td>
              <td className="border border-gray-300 p-2 text-right">{entry.unitPrice ?? entry.saleRate ?? 0}</td>
              <td className="border border-gray-300 p-2 text-right">{typeof entry.amount !== 'undefined' && entry.amount !== null && entry.amount !== '-' ? entry.amount : (() => { const qty = parseFloat(entry.qty ?? entry.quantity ?? 0); const rate = parseFloat(entry.unitPrice ?? entry.saleRate ?? 0); if (!isNaN(qty) && !isNaN(rate)) { const calcAmount = qty * rate; return isNaN(calcAmount) ? 0 : calcAmount; } return 0; })()}</td>
              <td className="border border-gray-300 p-2 text-right">{entry.discount ?? 0}</td>
              <td className="border border-gray-300 p-2 text-right">{typeof entry.total !== 'undefined' && entry.total !== null && entry.total !== '-' ? entry.total : (() => { const qty = parseFloat(entry.qty ?? entry.quantity ?? 0); const rate = parseFloat(entry.unitPrice ?? entry.saleRate ?? 0); const discount = parseFloat(entry.discount ?? 0); if (!isNaN(qty) && !isNaN(rate)) { const calcTotal = qty * rate - discount; return isNaN(calcTotal) ? 0 : calcTotal.toLocaleString(); } return 0; })()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Add a print view component for Project Delivery Invoice Report that matches the on-screen table
const ProjectInvoiceReportScreenLikePrintView = ({ data, customers, projects, currencySymbol }: { data: any, customers: any[], projects: any[], currencySymbol: string }) => {
  const filteredProjectList = data.filteredProjectList || [];
  return (
    <div className="font-['Times_New_Roman'] text-[var(--color-text)] p-6">
      <h1 className="text-2xl font-bold mb-4">Project Delivery Invoice Report</h1>
      <table className="min-w-full border-collapse text-base">
        <thead>
          <tr className="bg-gray-100">
            <th className="border border-gray-300 p-2">Invoice Number</th>
            <th className="border border-gray-300 p-2">Date</th>
            <th className="border border-gray-300 p-2">Customer</th>
            <th className="border border-gray-300 p-2">Project</th>
            <th className="border border-gray-300 p-2">Total ({currencySymbol})</th>
            <th className="border border-gray-300 p-2">Items</th>
          </tr>
        </thead>
        <tbody>
          {filteredProjectList.length === 0 ? (
            <tr><td colSpan={6} className="text-center text-gray-500 italic p-6">No delivery invoices found for the selected filters or period.</td></tr>
          ) : (
            filteredProjectList.map((inv: any, idx: number) => {
              const project = projects.find((p: any) => p._id === (inv.projectId || inv.project?._id));
              return (
                <tr key={inv._id || idx}>
                  <td className="border border-gray-300 p-2">{inv.invoiceNumber}</td>
                  <td className="border border-gray-300 p-2">{inv.date}</td>
                  <td className="border border-gray-300 p-2">{typeof inv.customer === 'object' ? inv.customer?.name || '-' : inv.customer || '-'}</td>
                  <td className="border border-gray-300 p-2">{project?.name || inv.project?.name || '-'}</td>
                  <td className="border border-gray-300 p-2 text-right">{currencySymbol}{inv.total?.toLocaleString?.() ?? 0}</td>
                  <td className="border border-gray-300 p-2">
                    <table className="min-w-full divide-y divide-gray-100 bg-gray-50">
                      <thead>
                        <tr>
                          <th className="px-2 py-1 text-xs font-medium text-gray-500 uppercase tracking-wider font-['Times_New_Roman']">Product</th>
                          <th className="px-2 py-1 text-xs font-medium text-gray-500 uppercase tracking-wider font-['Times_New_Roman']">Unit</th>
                          <th className="px-2 py-1 text-xs font-medium text-gray-500 uppercase tracking-wider font-['Times_New_Roman']">Qty</th>
                          <th className="px-2 py-1 text-xs font-medium text-gray-500 uppercase tracking-wider font-['Times_New_Roman']">Rate</th>
                          <th className="px-2 py-1 text-xs font-medium text-gray-500 uppercase tracking-wider font-['Times_New_Roman']">Amount</th>
                          <th className="px-2 py-1 text-xs font-medium text-gray-500 uppercase tracking-wider font-['Times_New_Roman']">Discount</th>
                          <th className="px-2 py-1 text-xs font-medium text-gray-500 uppercase tracking-wider font-['Times_New_Roman']">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(inv.items || []).map((item: any, itemIdx: number) => (
                          <tr key={itemIdx}>
                            <td className="px-2 py-1 font-['Times_New_Roman']">{item.name || item.product?.name || '-'}</td>
                            <td className="px-2 py-1 font-['Times_New_Roman']">{item.unit || item.product?.unit || '-'}</td>
                            <td className="px-2 py-1 font-['Times_New_Roman']">{item.qty ?? item.quantity ?? '-'}</td>
                            <td className="px-2 py-1 font-['Times_New_Roman']">{item.unitPrice ?? item.saleRate ?? '-'}</td>
                            <td className="px-2 py-1 font-['Times_New_Roman']">
                              {typeof item.amount !== 'undefined' && item.amount !== null && item.amount !== '-' 
                                ? item.amount 
                                : (() => {
                                    const qty = parseFloat(item.qty ?? item.quantity ?? 0);
                                    const rate = parseFloat(item.unitPrice ?? item.saleRate ?? 0);
                                    if (!isNaN(qty) && !isNaN(rate)) {
                                      const calcAmount = qty * rate;
                                      return isNaN(calcAmount) ? 0 : calcAmount;
                                    }
                                    return 0;
                                  })()
                              }
                            </td>
                            <td className="px-2 py-1 font-['Times_New_Roman']">{item.discount ?? 0}</td>
                            <td className="px-2 py-1 font-['Times_New_Roman']">{typeof item.total !== 'undefined' && item.total !== null && item.total !== '-' ? item.total : (() => { const qty = parseFloat(item.qty ?? item.quantity ?? 0); const rate = parseFloat(item.unitPrice ?? item.saleRate ?? 0); const discount = parseFloat(item.discount ?? 0); if (!isNaN(qty) && !isNaN(rate)) { const calcTotal = qty * rate - discount; return isNaN(calcTotal) ? 0 : calcTotal.toLocaleString(); } return 0; })()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};

const ReportsPage: React.FC = () => {
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [selectedReport, setSelectedReport] = useState('sales');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedSubCategory, setSelectedSubCategory] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [selectedBrand, setSelectedBrand] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [productStockData, setProductStockData] = useState<ReportData['productStockReport'] | null>(null);
  const [productStockLoading, setProductStockLoading] = useState(false);
  const [categories, setCategories] = useState<any[]>([]);
  const [subCategories, setSubCategories] = useState<any[]>([]);
  const [locations, setLocations] = useState<any[]>([]);
  const [brands, setBrands] = useState<any[]>([]);
  const [purchaseInvoices, setPurchaseInvoices] = useState<any[]>([]);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [supplierReportLoading, setSupplierReportLoading] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [detailsModalData, setDetailsModalData] = useState<{ projectName: string; productName: string; entries: any[] } | null>(null);
  const [selectedSupplierInvoice, setSelectedSupplierInvoice] = useState('');
  // --- Add state for customers, projects, selected customer/project, and delivery invoices ---
  const [customers, setCustomers] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [selectedProject, setSelectedProject] = useState('');
  const [deliveryInvoices, setDeliveryInvoices] = useState<any[]>([]);
  const [returnInvoices, setReturnInvoices] = useState<any[]>([]);
  const [projectReportLoading, setProjectReportLoading] = useState(false);
  const [showDeliveryInvoiceModal, setShowDeliveryInvoiceModal] = useState(false);
  const [deliveryInvoiceData, setDeliveryInvoiceData] = useState<any>(null);
  const [deliveryInvoiceLoading, setDeliveryInvoiceLoading] = useState(false);
  const { settings } = useSettings();
  const currencySymbol = settings?.invoice?.defaultCurrencySymbol || '৳';

  // Function to calculate returned quantity for a specific delivery invoice item
  const getReturnedQuantity = (deliveryInvoiceId: string, productId: string) => {
    const returnInvoicesForDelivery = returnInvoices.filter(returnInv => 
      returnInv.deliveryInvoiceId === deliveryInvoiceId || 
      returnInv.deliveryInvoiceId?._id === deliveryInvoiceId
    );
    
    let totalReturned = 0;
    returnInvoicesForDelivery.forEach((returnInv: any) => {
      returnInv.items?.forEach((item: any) => {
        const itemProductId = item.productId || item.product?._id || item.product;
        if (itemProductId === productId) {
          totalReturned += item.qty || item.quantity || 0;
        }
      });
    });
    
    return totalReturned;
  };

  // Add refs for print sections
  const salesReportRef = useRef<HTMLDivElement>(null);
  const inventoryReportRef = useRef<HTMLDivElement>(null);
  const productStockReportRef = useRef<HTMLDivElement>(null);
  const supplierPurchaseReportRef = useRef<HTMLDivElement>(null);
  const supplierInvoiceReportRef = useRef<HTMLDivElement>(null);
  const projectInvoiceReportRef = useRef<HTMLDivElement>(null);
  const projectProductReportRef = useRef<HTMLDivElement>(null);
  const detailsModalPrintRef = useRef<HTMLDivElement>(null);
  const deliveryInvoiceModalPrintRef = useRef<HTMLDivElement>(null);

  // Add state for print modal
  const [printModalOpen, setPrintModalOpen] = useState(false);
  const [printModalData, setPrintModalData] = useState<{ type: string; data: any } | null>(null);

  // Add state for details modal for supplier product purchase report
  const [supplierProductDetailsModalOpen, setSupplierProductDetailsModalOpen] = useState(false);
  const [supplierProductDetailsModalData, setSupplierProductDetailsModalData] = useState<any>(null);

  // Print handler for each report
  function handlePrintOverlay(reportType: string, data: any) {
    setPrintModalData({ type: reportType, data });
    setPrintModalOpen(true);
  }

  function handlePrint(ref: React.RefObject<HTMLDivElement>) {
    if (!ref.current) {
      alert('Nothing to print: the selected section is empty.');
      return;
    }
    const printContents = ref.current.innerHTML;
    const printWindow = window.open('', '', 'height=800,width=1200');
    if (!printWindow) return;
    printWindow.document.write('<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8"><title>Print Report</title>');
    // Copy all stylesheets and style tags from the main document
    Array.from(document.head.querySelectorAll('style,link[rel="stylesheet"]')).forEach((node) => {
      printWindow.document.write(node.outerHTML);
    });
    printWindow.document.write('</head><body>');
    printWindow.document.write(printContents);
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.focus();
    printWindow.onload = function() {
      printWindow.print();
      // Do not close the window automatically; let the user close it manually
    };
  }

  const fetchReportData = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (dateRange.start) params.append('startDate', dateRange.start);
      if (dateRange.end) params.append('endDate', dateRange.end);
      
      const response = await apiService.get(`/reports/dashboard?${params}`);
      setReportData(response.data);
    } catch (error) {
      console.error('Error fetching report data:', error);
      toast.error('Failed to fetch report data');
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  const fetchProductStockData = useCallback(async () => {
    try {
      setProductStockLoading(true);
      const params = new URLSearchParams();
      if (selectedCategory !== 'all') {
        params.append('category', selectedCategory);
      }
      if (selectedSubCategory !== 'all') {
        params.append('subcategory', selectedSubCategory);
      }
      if (selectedLocation !== 'all') {
        params.append('location', selectedLocation);
      }
      if (selectedBrand !== 'all') {
        params.append('brand', selectedBrand);
      }
      if (selectedStatus !== 'all') {
        params.append('status', selectedStatus);
      }
      
      const response = await apiService.get(`/reports/product-stock?${params}`);
      setProductStockData(response.data);
    } catch (error) {
      console.error('Error fetching product stock data:', error);
      toast.error('Failed to fetch product stock data');
    } finally {
      setProductStockLoading(false);
    }
  }, [selectedCategory, selectedSubCategory, selectedLocation, selectedBrand, selectedStatus]);

  // Fetch purchase invoices and suppliers for supplier-product report
  useEffect(() => {
    if (selectedReport === 'supplier-purchase' || selectedReport === 'supplier-invoice') {
      setSupplierReportLoading(true);
      Promise.all([
        apiService.get('/invoices?type=purchase&startDate=' + dateRange.start + '&endDate=' + dateRange.end),
        apiService.get('/suppliers')
      ]).then(([invoicesRes, suppliersRes]) => {
        setPurchaseInvoices(invoicesRes.data.invoices || []);
        setSuppliers(suppliersRes.data.suppliers || []);
      }).catch(() => {
        toast.error('Failed to fetch purchase invoices or suppliers');
      }).finally(() => setSupplierReportLoading(false));
    }
  }, [selectedReport, dateRange]);

  // --- Fetch customers, projects, and delivery invoices when project reports are selected ---
  useEffect(() => {
    if (selectedReport === 'project-invoice' || selectedReport === 'project-product') {
      setProjectReportLoading(true);
      Promise.all([
        apiService.get('/customers'),
        apiService.get('/projects'),
        apiService.get('/invoices?type=delivery&startDate=' + dateRange.start + '&endDate=' + dateRange.end),
        apiService.get('/invoices?type=return&startDate=' + dateRange.start + '&endDate=' + dateRange.end)
      ]).then(([customersRes, projectsRes, invoicesRes, returnsRes]) => {
        setCustomers(customersRes.data.customers || []);
        setProjects(projectsRes.data.projects || []);
        setDeliveryInvoices(invoicesRes.data.invoices || []);
        setReturnInvoices(returnsRes.data.invoices || []);
      }).catch(() => {
        toast.error('Failed to fetch delivery invoices, customers, or projects');
      }).finally(() => setProjectReportLoading(false));
    }
  }, [selectedReport, dateRange]);

  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);

  // Listen for global soft refresh and refetch current report data without reloading
  useEffect(() => {
    const onAppRefresh = () => {
      fetchReportData();
      if (selectedReport === 'product-stock') {
        fetchProductStockData();
      }
      if (selectedReport === 'supplier-purchase' || selectedReport === 'supplier-invoice') {
        // Trigger supplier effect by toggling selectedReport to same value
        setSupplierReportLoading(true);
        Promise.all([
          apiService.get('/invoices?type=purchase&startDate=' + dateRange.start + '&endDate=' + dateRange.end),
          apiService.get('/suppliers')
        ]).then(([invoicesRes, suppliersRes]) => {
          setPurchaseInvoices(invoicesRes.data.invoices || []);
          setSuppliers(suppliersRes.data.suppliers || []);
        }).finally(() => setSupplierReportLoading(false));
      }
      if (selectedReport === 'project-invoice' || selectedReport === 'project-product') {
        setProjectReportLoading(true);
        Promise.all([
          apiService.get('/customers'),
          apiService.get('/projects'),
          apiService.get('/invoices?type=delivery&startDate=' + dateRange.start + '&endDate=' + dateRange.end),
          apiService.get('/invoices?type=return&startDate=' + dateRange.start + '&endDate=' + dateRange.end)
        ]).then(([customersRes, projectsRes, invoicesRes, returnsRes]) => {
          setCustomers(customersRes.data.customers || []);
          setProjects(projectsRes.data.projects || []);
          setDeliveryInvoices(invoicesRes.data.invoices || []);
          setReturnInvoices(returnsRes.data.invoices || []);
        }).finally(() => setProjectReportLoading(false));
      }
    };
    window.addEventListener('app:refresh', onAppRefresh as any);
    return () => window.removeEventListener('app:refresh', onAppRefresh as any);
  }, [selectedReport, fetchReportData, fetchProductStockData, dateRange]);

        useEffect(() => {
          if (selectedReport === 'product-stock') {
            fetchProductStockData();
            // Fetch filter options
            Promise.all([
              apiService.get('/categories/all'),
              apiService.get('/brands/all'),
              apiService.get('/locations/all')
            ]).then(([categoriesRes, brandsRes, locationsRes]) => {
              // Build category hierarchy - filter parent categories and attach children
              const allCategories = categoriesRes.data || [];
              const parentCategories = allCategories.filter((cat: any) => !cat.parentCategory);
              
              // Attach children to parent categories
              const categoriesWithChildren = parentCategories.map((parent: any) => ({
                ...parent,
                children: allCategories.filter((cat: any) => 
                  cat.parentCategory && cat.parentCategory._id === parent._id
                )
              }));
              
              setCategories(categoriesWithChildren);
              setBrands(brandsRes.data || []);
              setLocations(locationsRes.data || []);
            }).catch(() => {
              toast.error('Failed to fetch filter options');
            });
          }
        }, [selectedReport, fetchProductStockData]);

        // Re-fetch data when filters change
        useEffect(() => {
          if (selectedReport === 'product-stock') {
            fetchProductStockData();
          }
        }, [selectedReport, selectedCategory, selectedSubCategory, selectedLocation, selectedBrand, selectedStatus, fetchProductStockData]);

  // Update subcategories when selected category changes
  useEffect(() => {
    if (selectedCategory !== 'all') {
      const parentCategory = categories.find(cat => cat._id === selectedCategory);
      if (parentCategory && parentCategory.children) {
        setSubCategories(parentCategory.children);
      } else {
        setSubCategories([]);
      }
      setSelectedSubCategory('all');
    } else {
      setSubCategories([]);
      setSelectedSubCategory('all');
    }
  }, [selectedCategory, categories]);

  const exportReport = async (reportType: string) => {
    try {
      setExporting(reportType);
      const params = new URLSearchParams();
      if (dateRange.start) params.append('startDate', dateRange.start);
      if (dateRange.end) params.append('endDate', dateRange.end);
      
      if (reportType === 'product-stock') {
        if (selectedCategory !== 'all') params.append('category', selectedCategory);
        if (selectedSubCategory !== 'all') params.append('subcategory', selectedSubCategory);
        if (selectedLocation !== 'all') params.append('location', selectedLocation);
        if (selectedBrand !== 'all') params.append('brand', selectedBrand);
        if (selectedStatus !== 'all') params.append('status', selectedStatus);
      } else if (selectedCategory !== 'all') {
        params.append('category', selectedCategory);
      }
      
      await apiService.download(`/reports/${reportType}/export?${params}`, `${reportType}-report.xlsx`);
      toast.success(`${reportType} report exported successfully`);
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Failed to export report');
    } finally {
      setExporting(null);
    }
  };

  const getStockStatusColor = (status: string) => {
    switch (status) {
      case 'Out of Stock': return 'bg-red-100 text-red-800';
      case 'Low Stock': return 'bg-yellow-100 text-yellow-800';
      case 'In Stock': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const StatCard: React.FC<{
    title: string;
    value: string | number;
    icon: React.ReactNode;
    color: string;
    change?: string;
  }> = ({ title, value, icon, color, change }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 } as any}
      animate={{ opacity: 1, y: 0 } as any}
      className={`bg-[var(--color-surface)] rounded-lg shadow-md p-4 md:p-6 border-l-4 ${color}`}
      style={{ borderRadius: 'var(--border-radius)' } as unknown as React.CSSProperties}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-xs md:text-sm font-medium text-[var(--color-text-secondary)] truncate" style={{ color: 'var(--color-text-secondary)', fontFamily: 'var(--font-family)' } as unknown as React.CSSProperties}>{title}</p>
          <p className="text-lg md:text-2xl font-bold text-[var(--color-text)] truncate" style={{ color: 'var(--color-text)', fontFamily: 'var(--font-family)' } as unknown as React.CSSProperties}>{value}</p>
          {change && (
            <p className="text-xs md:text-sm text-[var(--color-success)] font-['Times_New_Roman']">{change}</p>
          )}
        </div>
        <div className="p-2 md:p-3 bg-[var(--color-primary-light)] rounded-full ml-2 flex-shrink-0">
          {icon}
        </div>
      </div>
    </motion.div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!reportData) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2 font-['Times_New_Roman']">
            No report data available
          </h3>
          <p className="text-gray-600 font-['Times_New_Roman']">
            Try refreshing the page or check your connection
          </p>
          <button
            onClick={fetchReportData}
            className="mt-4 flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-['Times_New_Roman'] mx-auto"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh Data
          </button>
        </div>
      </div>
    );
  }

  // --- Moved report data aggregations above JSX for print and render ---
  // Supplier Product Purchase Report
  type SupplierProductMap = { [productName: string]: { qty: number; unit: string } };
  type SupplierMap = { [supplierId: string]: { name: string; products: SupplierProductMap } };
  const supplierMap: SupplierMap = {};
  const detailsMap: { [key: string]: any[] } = {};
  purchaseInvoices.forEach((inv: any) => {
    const supplierId = inv.customer?._id || inv.supplier?._id || suppliers.find((s: any) => s.name === (inv.customer?.name || inv.supplier?.name || inv.customerName))?._id || 'unknown';
    const supplierName = inv.customer?.name || inv.supplier?.name || inv.customerName || 'Unknown';
    if (!supplierMap[supplierId]) supplierMap[supplierId] = { name: supplierName, products: {} };
    (inv.items || []).forEach((item: any) => {
      const productName = item.name || item.product?.name || 'Unknown';
      const unit = item.unit || item.product?.unit || '-';
      if (!supplierMap[supplierId].products[productName]) {
        supplierMap[supplierId].products[productName] = { qty: 0, unit };
      }
      if (supplierMap[supplierId].products[productName].unit === '-' && unit !== '-') {
        supplierMap[supplierId].products[productName].unit = unit;
      }
      const qty = item.qty ?? item.quantity ?? 0;
      supplierMap[supplierId].products[productName].qty += qty;
      const key = supplierId + '||' + productName;
      if (!detailsMap[key]) detailsMap[key] = [];
      detailsMap[key].push({
        invoiceNumber: inv.invoiceNumber,
        date: inv.createdAt ? inv.createdAt.split('T')[0] : '',
        qty,
        unit,
        rate: item.purchaseRate ?? item.unitPrice ?? '-',
        amount: item.amount ?? '-',
        discount: item.discount ?? 0,
        total: item.total ?? item.totalPrice ?? '-',
        ...item,
      });
    });
  });
  const supplierList = selectedSupplier
    ? [supplierMap[selectedSupplier]].filter(Boolean)
    : Object.values(supplierMap);

  // Supplier Purchase Invoice Report
  const supplierInvoiceMap: { [supplierId: string]: { name: string; invoices: any[] } } = {};
  purchaseInvoices.forEach((inv: any) => {
    const supplierName = inv.customer?.name || inv.customerName || 'Unknown';
    const supplierId = suppliers.find((s: any) => s.name === supplierName)?._id || 'unknown';
    if (selectedSupplierInvoice && supplierId !== selectedSupplierInvoice) return;
    if (!supplierInvoiceMap[supplierId]) supplierInvoiceMap[supplierId] = { name: supplierName, invoices: [] };
    supplierInvoiceMap[supplierId].invoices.push(inv);
  });
  // Flatten all invoices into a single array for display (no supplier grouping)
  const allSupplierInvoices = Object.values(supplierInvoiceMap).flatMap(s => s.invoices);
  const filteredSupplierInvoices = selectedSupplierInvoice
    ? allSupplierInvoices.filter(inv => {
        const supplierName = inv.customer?.name || inv.customerName || 'Unknown';
        const supplierId = suppliers.find((s: any) => s.name === supplierName)?._id || 'unknown';
        return supplierId === selectedSupplierInvoice;
      })
    : allSupplierInvoices;

  // Project-wise Delivery Invoice Report
  let filteredProjectList: any[] = [];
  let projectInvoicePrintData: { projects: any[] } = { projects: [] };
  if (selectedReport === 'project-invoice') {
    let filteredInvoices = deliveryInvoices;
    if (selectedCustomer) {
      const customer = customers.find((c: any) => c._id === selectedCustomer);
      const customerProjectIds = projects.filter((p: any) => p.customerName === customer?.name).map((p: any) => p._id);
      filteredInvoices = filteredInvoices.filter((inv: any) => {
        const pid = inv.projectId || inv.project?._id;
        return customerProjectIds.includes(pid);
      });
    }
    if (selectedProject) {
      filteredInvoices = filteredInvoices.filter((inv: any) => {
        const pid = inv.projectId || inv.project?._id;
        return pid === selectedProject;
      });
    }
    filteredProjectList = filteredInvoices;
    // Group by project for print
    const projectMap: { [projectId: string]: { _id: string; name: string; invoices: any[] } } = {};
    filteredInvoices.forEach((inv: any) => {
      const pid = inv.projectId || inv.project?._id;
      const project = projects.find((p: any) => p._id === pid);
      if (!pid || !project) return;
      if (!projectMap[pid]) {
        projectMap[pid] = { _id: pid, name: project.name, invoices: [] };
      }
      projectMap[pid].invoices.push({
        _id: inv._id,
        invoiceNumber: inv.invoiceNumber,
        date: inv.createdAt ? new Date(inv.createdAt).toLocaleDateString() : '-',
        customer: inv.customerName || inv.customer?.name || '-',
        total: inv.total,
      });
    });
    projectInvoicePrintData = { projects: Object.values(projectMap) };
  }

  // Project-wise Product Delivery Report
  let productList: any[] = [];
  if (selectedReport === 'project-product') {
    let filteredInvoices = deliveryInvoices;
    if (selectedCustomer) {
      const customer = customers.find((c: any) => c._id === selectedCustomer);
      const customerProjectIds = projects.filter((p: any) => p.customerName === customer?.name).map((p: any) => p._id);
      filteredInvoices = filteredInvoices.filter((inv: any) => {
        const pid = inv.projectId || inv.project?._id;
        return customerProjectIds.includes(pid);
      });
    }
    if (selectedProject) {
      filteredInvoices = filteredInvoices.filter((inv: any) => {
        const pid = inv.projectId || inv.project?._id;
        return pid === selectedProject;
      });
    }
    // Aggregate all products across all filtered invoices
    const productMap: { [productName: string]: { qty: number; unit: string; total: number; details: any[] } } = {};
    filteredInvoices.forEach((inv: any) => {
      (inv.items || []).forEach((item: any) => {
        const productName = item.name || item.product?.name || '-';
        if (!productMap[productName]) {
          productMap[productName] = { qty: 0, unit: item.unit || item.product?.unit || '-', total: 0, details: [] };
        }
        productMap[productName].qty += Number(item.qty ?? item.quantity ?? 0);
        productMap[productName].total += Number(item.total ?? item.totalPrice ?? 0);
        productMap[productName].details.push({
          _id: inv._id,
          invoiceNumber: inv.invoiceNumber,
          date: inv.createdAt ? new Date(inv.createdAt).toLocaleDateString() : '-',
          customer: inv.customerName || inv.customer?.name || '-',
          project: (() => {
            const project = projects.find((p: any) => p._id === (inv.projectId || inv.project?._id));
            return project?.name || inv.project?.name || '-';
          })(),
          ...item,
        });
      });
    });
    productList = Object.entries(productMap).map(([productName, { qty, unit, total, details }]) => ({ productName, qty, unit, total, details }));
  }

  // Flatten all supplier products into a single array for the table
  // Deduplicate by productName + unit (and optionally supplierName if needed)
  const allSupplierProductsRaw = Object.entries(supplierMap).flatMap(([supId, sup]: any) =>
    Object.entries(sup.products || {}).map(([productName, { qty, unit }]: any) => {
      const supplierId = supId;
      const key = supplierId + '||' + productName;
      const details = detailsMap[key] || [];
      const invoiceIds = details.map((d: any) => d.invoiceId || d._id).filter(Boolean);
      return {
        productName,
        unit,
        qty,
        details,
        supplierName: sup.name,
        supplierId,
        invoiceIds,
      };
    })
  );

  // Deduplicate products by productName + unit
  const allSupplierProductsMap = new Map();
  allSupplierProductsRaw.forEach((row) => {
    const key = row.productName + '||' + row.unit;
    if (!allSupplierProductsMap.has(key)) {
      allSupplierProductsMap.set(key, { ...row });
    } else {
      // Sum quantities and merge details/invoiceIds
      const existing = allSupplierProductsMap.get(key);
      existing.qty += row.qty;
      existing.details = existing.details.concat(row.details);
      existing.invoiceIds = Array.from(new Set(existing.invoiceIds.concat(row.invoiceIds)));
      allSupplierProductsMap.set(key, existing);
    }
  });
  const allSupplierProducts = Array.from(allSupplierProductsMap.values());

  // Print view for Supplier Product Purchase Report (flat, unsegmented)
  const SupplierProductPurchaseReportPrintView = ({ data, currencySymbol }: { data: any, currencySymbol: string }) => {
    const allSupplierProducts = data?.allSupplierProducts || [];
    return (
      <div className="font-['Times_New_Roman'] text-[var(--color-text)] p-6">
        <h1 className="text-2xl font-bold mb-4">Supplier Product Purchase Report</h1>
        <table className="min-w-full border-collapse text-sm">
          <thead>
            <tr className="bg-gray-100">
              <th className="border border-gray-300 p-2">Product Name</th>
              <th className="border border-gray-300 p-2">Unit</th>
              <th className="border border-gray-300 p-2">Total Quantity Purchased</th>
            </tr>
          </thead>
          <tbody>
            {allSupplierProducts.length === 0 ? (
              <tr>
                <td colSpan={3} className="text-center text-gray-500 italic p-6">No purchase data available for the selected supplier or period.</td>
              </tr>
            ) : (
              <>
                {allSupplierProducts.map((row: any, idx: number) => (
                  <tr key={idx}>
                    <td className="border border-gray-300 p-2">{row.productName || '-'}</td>
                    <td className="border border-gray-300 p-2">{row.unit || '-'}</td>
                    <td className="border border-gray-300 p-2 text-center">{row.qty ?? 0}</td>
                  </tr>
                ))}
              </>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  // Filter by selectedSupplier if set
  const filteredSupplierProductsRaw = selectedSupplier
    ? allSupplierProductsRaw.filter(row => row.supplierId === selectedSupplier)
    : allSupplierProductsRaw;

  const filteredSupplierProductsMap = new Map();
  filteredSupplierProductsRaw.forEach((row) => {
    const key = row.productName + '||' + row.unit;
    if (!filteredSupplierProductsMap.has(key)) {
      filteredSupplierProductsMap.set(key, { ...row });
    } else {
      // Sum quantities and merge details/invoiceIds
      const existing = filteredSupplierProductsMap.get(key);
      existing.qty += row.qty;
      existing.details = existing.details.concat(row.details);
      existing.invoiceIds = Array.from(new Set(existing.invoiceIds.concat(row.invoiceIds)));
      filteredSupplierProductsMap.set(key, existing);
    }
  });
  const filteredSupplierProducts = Array.from(filteredSupplierProductsMap.values());

  return (
    <div className="no-dark">
      <div className="p-6 bg-[var(--color-background)] min-h-screen" style={{ fontFamily: 'var(--font-family)', color: 'var(--color-text)' } as unknown as React.CSSProperties}>
        {/* Print CSS to ensure only .print-mode is visible during print */}
        <style>{`
          @media print {
            @page { size: landscape; margin: 12mm; }
            html, body, .print-mode, .print-mode > div {
              width: 100% !important;
              max-width: 100% !important;
              box-sizing: border-box !important;
              margin: 0 !important;
              padding: 0 !important;
            }
            .print-mode table {
              width: 100% !important;
              max-width: 100% !important;
              box-sizing: border-box !important;
            }
            body.printing *:not(.print-mode):not(.print-mode *) {
              display: none !important;
            }
            .print-mode {
              display: block !important;
              position: static !important;
              box-shadow: none !important;
              background: white !important;
              color: black !important;
            }
          }
          .no-dark, .no-dark * {
            color-scheme: light !important;
            background: white !important;
            color: #222 !important;
            --color-background: #fff !important;
            --color-surface: #fff !important;
            --color-surface-light: #f9fafb !important;
            --color-text: #222 !important;
            --color-text-secondary: #555 !important;
          }
        `}</style>
        {/* Header */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl md:text-3xl font-bold" style={{ color: 'var(--color-text)', fontFamily: 'var(--font-family)' } as unknown as React.CSSProperties}>Reports</h1>
            <p style={{ color: 'var(--color-text-secondary)', fontFamily: 'var(--font-family)' } as unknown as React.CSSProperties} className="text-sm md:text-base">
              View and analyze your business reports
            </p>
          </div>
          <div className="flex space-x-3">
            <Button variant="secondary" onClick={fetchReportData} className="text-xs md:text-sm">
              Refresh
            </Button>
          </div>
        </div>

        {/* Date Range Filter */}
        <div className="bg-[var(--color-surface)] rounded-lg shadow-md p-4 md:p-6 mb-6">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-4 gap-4">
            <h2 className="text-base md:text-lg font-semibold text-gray-900 font-['Times_New_Roman']">Date Range</h2>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 w-full md:w-auto">
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-['Times_New_Roman'] bg-transparent text-[var(--color-text)] placeholder-[var(--color-text-secondary)] text-sm"
              />
              <span className="text-[var(--color-text-secondary)] font-['Times_New_Roman'] text-center sm:text-left">to</span>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-['Times_New_Roman'] bg-transparent text-[var(--color-text)] placeholder-[var(--color-text-secondary)] text-sm"
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {[
              { label: 'Last 7 Days', days: 7 },
              { label: 'Last 30 Days', days: 30 },
              { label: 'Last 90 Days', days: 90 },
              { label: 'This Month', days: 'thisMonth' },
              { label: 'Last Month', days: 'lastMonth' },
              { label: 'This Year', days: 'thisYear' }
            ].map((preset: { label: string; days: number | string }) => (
              <Button
                key={preset.label}
                onClick={() => {
                  const end = new Date();
                  let start = new Date();
                  
                  if (preset.days === 'thisMonth') {
                    start = new Date(end.getFullYear(), end.getMonth(), 1);
                  } else if (preset.days === 'lastMonth') {
                    start = new Date(end.getFullYear(), end.getMonth() - 1, 1);
                    const lastMonthEnd = new Date(end.getFullYear(), end.getMonth(), 0);
                    end.setTime(lastMonthEnd.getTime());
                  } else if (preset.days === 'thisYear') {
                    start = new Date(end.getFullYear(), 0, 1);
                  } else if (typeof preset.days === 'number') {
                    start.setDate(end.getDate() - preset.days);
                  }
                  
                  setDateRange({
                    start: start.toISOString().split('T')[0],
                    end: end.toISOString().split('T')[0]
                  });
                }}
                className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors font-['Times_New_Roman']"
              >
                {preset.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Summary Cards */}
        {reportData && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6">
            <StatCard
              title="Total Sales"
              value={`${currencySymbol}${reportData.salesReport.totalSales.toLocaleString()}`}
              icon={<TakaIcon className="w-4 h-4 md:w-6 md:h-6 text-green-600" />}
              color="border-green-500"
            />
            <StatCard
              title="Total Products"
              value={reportData.inventoryReport.totalProducts}
              icon={<Package className="w-4 h-4 md:w-6 md:h-6 text-blue-600" />}
              color="border-blue-500"
            />
            <StatCard
              title="Active Projects"
              value={reportData.projectReport.activeProjects}
              icon={<Users className="w-4 h-4 md:w-6 md:h-6 text-purple-600" />}
              color="border-purple-500"
            />
            <StatCard
              title="Inventory Value"
              value={`${currencySymbol}${reportData.inventoryReport.totalValue.toLocaleString()}`}
              icon={<TakaIcon className="w-4 h-4 md:w-6 md:h-6 text-yellow-600" />}
              color="border-yellow-500"
            />
          </div>
        )}

        {/* Report Type Tabs */}
        <div className="bg-[var(--color-surface)] rounded-lg shadow-md">
          <div className="border-b border-gray-200">
            <nav className="flex overflow-x-auto scrollbar-hide px-4 md:px-6 py-2">
              <div className="flex gap-2 md:gap-8 min-w-max">
              {[
                { id: 'sales', name: 'Sales', icon: <TrendingUp className="w-4 h-4" /> },
                { id: 'inventory', name: 'Inventory', icon: <Package className="w-4 h-4" /> },
                { id: 'projects', name: 'Project', icon: <Users className="w-4 h-4" /> },
                { id: 'product-stock', name: 'Product Stock', icon: <Database className="w-4 h-4" /> },
                { id: 'supplier-purchase', name: 'Supplier Product Purchase', icon: <Package className="w-4 h-4" /> },
                { id: 'supplier-invoice', name: 'Supplier Purchase Invoice', icon: <Package className="w-4 h-4" /> },
                { id: 'project-invoice', name: 'Project Delivery Invoice', icon: <Package className="w-4 h-4" /> },
                { id: 'project-product', name: 'Project Product Delivery', icon: <Package className="w-4 h-4" /> },
              ].map((tab) => (
                <Button
                  key={tab.id}
                  onClick={() => setSelectedReport(tab.id)}
                    className={`flex items-center py-2 md:py-4 px-2 md:px-1 border-b-2 font-medium text-xs md:text-sm font-['Times_New_Roman'] whitespace-nowrap flex-shrink-0 ${
                    selectedReport === tab.id
                      ? 'border-blue-500 text-[var(--color-text)]'
                      : 'border-transparent text-[var(--color-text-secondary)] hover:text-[var(--color-text)] hover:border-gray-300'
                  }`}
                >
                  {tab.icon}
                    <span className="ml-1 md:ml-2">{tab.name}</span>
                </Button>
              ))}
              </div>
            </nav>
          </div>

          <div className="p-6">
            {/* Sales Report */}
            {selectedReport === 'sales' && reportData && (
              <motion.div
                ref={salesReportRef}
                initial={{ opacity: 0 } as any}
                animate={{ opacity: 1 } as any}
                className="space-y-6"
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold text-gray-900 font-['Times_New_Roman']">Sales Overview</h3>
                  <div className="flex gap-2">
                    <Button variant="primary" onClick={() => handlePrintOverlay('sales', reportData)}>
                      <span className="mr-2">Print</span>
                      <Printer className="w-4 h-4" />
                    </Button>
                    <Button variant="secondary" onClick={() => exportReport('sales')} disabled={exporting === 'sales'}>
                      {exporting === 'sales' ? (
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4 mr-2" />
                      )}
                      {exporting === 'sales' ? 'Exporting...' : 'Export Report'}
                    </Button>
                  </div>
                </div>
                <div ref={salesReportRef}>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <StatCard
                      title="Total Sales"
                      value={`${currencySymbol}${reportData?.salesReport?.totalSales?.toLocaleString?.() ?? '0'}`}
                      icon={<TakaIcon className="w-6 h-6 text-green-600" />}
                      color="border-green-500"
                    />
                    <StatCard
                      title="Total Invoices"
                      value={reportData?.salesReport?.totalInvoices ?? 0}
                      icon={<Package className="w-6 h-6 text-blue-600" />}
                      color="border-blue-500"
                    />
                    <StatCard
                      title="Average Order Value"
                      value={`${currencySymbol}${reportData?.salesReport?.averageOrderValue?.toLocaleString?.() ?? '0'}`}
                      icon={<TakaIcon className="w-6 h-6 text-purple-600" />}
                      color="border-purple-500"
                    />
                  </div>

                  {/* Monthly Trends */}
                  {(reportData?.salesReport?.monthlyData ?? []).length > 0 && (
                    <div className="bg-[var(--color-surface)] rounded-lg p-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4 font-['Times_New_Roman']">Monthly Sales Trends</h4>
                      <div className="space-y-3">
                        {(reportData?.salesReport?.monthlyData ?? []).map((month, index) => (
                          <div key={index} className="flex items-center justify-between p-3 bg-[var(--color-surface)] rounded-lg">
                            <div>
                              <p className="font-medium text-gray-900 font-['Times_New_Roman']">{month.month}</p>
                              <p className="text-sm text-gray-600 font-['Times_New_Roman']">{month.invoices} invoices</p>
                            </div>
                            <p className="font-semibold text-blue-600 font-['Times_New_Roman']">
                              {currencySymbol}{month?.sales?.toLocaleString?.() ?? '0'}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Top Products */}
                  <div className="bg-[var(--color-surface)] rounded-lg p-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4 font-['Times_New_Roman']">Top Selling Products</h4>
                    <div className="space-y-3">
                      {(reportData?.salesReport?.topProducts ?? []).length > 0 ? (
                        (reportData?.salesReport?.topProducts ?? []).map((product, index) => (
                          <div key={index} className="flex items-center justify-between p-3 bg-[var(--color-surface)] rounded-lg">
                            <div>
                              <p className="font-medium text-gray-900 font-['Times_New_Roman']">{product.name}</p>
                              <p className="text-sm text-gray-600 font-['Times_New_Roman']">{product.quantity} units sold</p>
                            </div>
                            <p className="font-semibold text-green-600 font-['Times_New_Roman']">
                              {currencySymbol}{product?.revenue?.toLocaleString?.() ?? '0'}
                            </p>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-8 text-gray-500 font-['Times_New_Roman']">
                          No sales data available for the selected period
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Inventory Report */}
            {selectedReport === 'inventory' && reportData && (
              <motion.div
                ref={inventoryReportRef}
                initial={{ opacity: 0 } as any}
                animate={{ opacity: 1 } as any}
                className="space-y-6"
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold text-gray-900 font-['Times_New_Roman']">Inventory Overview</h3>
                  <div className="flex gap-2">
                    <Button variant="primary" onClick={() => handlePrintOverlay('inventory', reportData)}>
                      <span className="mr-2">Print</span>
                      <Printer className="w-4 h-4" />
                    </Button>
                    <Button variant="secondary" onClick={() => exportReport('inventory')} disabled={exporting === 'inventory'}>
                      {exporting === 'inventory' ? (
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4 mr-2" />
                      )}
                      {exporting === 'inventory' ? 'Exporting...' : 'Export Report'}
                    </Button>
                  </div>
                </div>
                <div ref={inventoryReportRef}>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    <StatCard
                      title="Total Products"
                      value={reportData?.inventoryReport?.totalProducts ?? 0}
                      icon={<Package className="w-6 h-6 text-blue-600" />}
                      color="border-blue-500"
                    />
                    <StatCard
                      title="Low Stock Items"
                      value={reportData?.inventoryReport?.lowStockProducts ?? 0}
                      icon={<AlertCircle className="w-6 h-6 text-yellow-600" />}
                      color="border-yellow-500"
                    />
                    <StatCard
                      title="Out of Stock"
                      value={reportData?.inventoryReport?.outOfStockProducts ?? 0}
                      icon={<AlertCircle className="w-6 h-6 text-red-600" />}
                      color="border-red-500"
                    />
                    <StatCard
                      title="Total Inventory Value"
                      value={`${currencySymbol}${reportData?.inventoryReport?.totalValue?.toLocaleString?.() ?? '0'}`}
                      icon={<TakaIcon className="w-6 h-6 text-green-600" />}
                      color="border-green-500"
                    />
                  </div>

                  {/* Top Categories by Parent Category */}
                  <div className="bg-[var(--color-surface)] rounded-lg p-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4 font-['Times_New_Roman']">Top Parent Categories by Value</h4>
                    <div className="space-y-3">
                      {(reportData?.inventoryReport?.topCategories ?? []).length > 0 ? (
                        (reportData?.inventoryReport?.topCategories ?? []).map((category, index) => (
                          <div key={index} className="flex items-center justify-between p-3 bg-[var(--color-surface)] rounded-lg">
                            <div className="flex-1">
                              <p className="font-medium text-gray-900 font-['Times_New_Roman']">{category.name}</p>
                              <div className="flex items-center gap-4 mt-1">
                                <p className="text-sm text-gray-600 font-['Times_New_Roman']">{category.productCount} products</p>
                                {category.lowStockCount > 0 && (
                                  <p className="text-sm text-yellow-600 font-['Times_New_Roman'] flex items-center gap-1">
                                    <AlertCircle className="w-3 h-3" />
                                    {category.lowStockCount} low stock
                                  </p>
                                )}
                                {category.outOfStockCount > 0 && (
                                  <p className="text-sm text-red-600 font-['Times_New_Roman'] flex items-center gap-1">
                                    <AlertCircle className="w-3 h-3" />
                                    {category.outOfStockCount} out of stock
                                  </p>
                                )}
                              </div>
                            </div>
                            <p className="font-semibold text-blue-600 font-['Times_New_Roman']">
                              {currencySymbol}{category?.totalValue?.toLocaleString?.() ?? '0'}
                            </p>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-8 text-gray-500 font-['Times_New_Roman']">
                          No inventory data available
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Project Report */}
            {selectedReport === 'projects' && reportData && (
              <motion.div
                ref={projectInvoiceReportRef}
                initial={{ opacity: 0 } as any}
                animate={{ opacity: 1 } as any}
                className="space-y-6"
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold text-gray-900 font-['Times_New_Roman']">Project Overview</h3>
                  <Button variant="secondary" onClick={() => exportReport('projects')} disabled={exporting === 'projects'}>
                    {exporting === 'projects' ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-2" />
                    )}
                    {exporting === 'projects' ? 'Exporting...' : 'Export Report'}
                  </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <StatCard
                    title="Total Projects"
                    value={reportData?.projectReport?.totalProjects ?? 0}
                    icon={<Users className="w-6 h-6 text-blue-600" />}
                    color="border-blue-500"
                  />
                  <StatCard
                    title="Active Projects"
                    value={reportData?.projectReport?.activeProjects ?? 0}
                    icon={<TrendingUp className="w-6 h-6 text-green-600" />}
                    color="border-green-500"
                  />
                  <StatCard
                    title="Completed Projects"
                    value={reportData?.projectReport?.completedProjects ?? 0}
                    icon={<CheckCircle className="w-6 h-6 text-purple-600" />}
                    color="border-purple-500"
                  />
                  <StatCard
                    title="Total Budget"
                    value={`${currencySymbol}${reportData?.projectReport?.totalBudget?.toLocaleString?.() ?? '0'}`}
                    icon={<TakaIcon className="w-6 h-6 text-yellow-600" />}
                    color="border-yellow-500"
                  />
                </div>

                {/* Project Status */}
                <div className="bg-[var(--color-surface)] rounded-lg p-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 font-['Times_New_Roman']">Project Status Distribution</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {(reportData?.projectReport?.projectStatus ?? []).length > 0 ? (
                      (reportData?.projectReport?.projectStatus ?? []).map((status, index) => (
                        <div key={index} className="bg-[var(--color-surface)] rounded-lg p-4">
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-gray-900 font-['Times_New_Roman'] capitalize">{status.status}</span>
                            <span className="text-2xl font-bold text-blue-600 font-['Times_New_Roman']">{status.count}</span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="col-span-full text-center py-8 text-gray-500 font-['Times_New_Roman']">
                        No project data available
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Product Stock Report */}
            {selectedReport === 'product-stock' && (
              <motion.div
                ref={productStockReportRef}
                initial={{ opacity: 0 } as any}
                animate={{ opacity: 1 } as any}
                className="space-y-6"
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold text-gray-900 font-['Times_New_Roman']">Product Stock Report</h3>
                  <div className="flex gap-2">
                    <button onClick={() => { fetchReportData(); if (selectedReport === 'product-stock') fetchProductStockData(); }} title="Refresh" className="flex items-center justify-center p-2 rounded-lg hover:bg-gray-50 transition-colors bg-transparent">
                      <RefreshCw className="w-4 h-4" />
                    </button>
                    <Button variant="primary" onClick={() => handlePrintOverlay('product-stock', { productStockReport: productStockData })}>
                      <span className="mr-2">Print</span>
                      <Printer className="w-4 h-4" />
                    </Button>
                    <Button variant="secondary" onClick={() => exportReport('product-stock')} disabled={exporting === 'product-stock'}>
                      {exporting === 'product-stock' ? (
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4 mr-2" />
                      )}
                      {exporting === 'product-stock' ? 'Exporting...' : 'Export Report'}
                    </Button>
                  </div>
                </div>
                <div ref={productStockReportRef}>
                  {productStockLoading ? (
                    <div className="flex items-center justify-center h-64">
                      <LoadingSpinner size="lg" />
                    </div>
                  ) : productStockData ? (
                    <>
                      {/* Summary Cards */}
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                        <StatCard
                          title="Total Products"
                          value={productStockData.summary.totalProducts}
                          icon={<Package className="w-6 h-6 text-blue-600" />}
                          color="border-blue-500"
                        />
                        <StatCard
                          title="In Stock"
                          value={productStockData.summary.inStockProducts}
                          icon={<CheckCircle className="w-6 h-6 text-green-600" />}
                          color="border-green-500"
                        />
                        <StatCard
                          title="Low Stock"
                          value={productStockData.summary.lowStockProducts}
                          icon={<AlertCircle className="w-6 h-6 text-yellow-600" />}
                          color="border-yellow-500"
                        />
                        <StatCard
                          title="Out of Stock"
                          value={productStockData.summary.outOfStockProducts}
                          icon={<AlertCircle className="w-6 h-6 text-red-600" />}
                          color="border-red-500"
                        />
                      </div>

                      {/* Category Summary */}
                      {productStockData.categorySummary.length > 0 && (
                        <div className="bg-[var(--color-surface)] rounded-lg p-6">
                          <h4 className="text-lg font-semibold text-gray-900 mb-4 font-['Times_New_Roman']">Category Summary</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {productStockData.categorySummary.map((category) => (
                              <div key={category._id} className="bg-[var(--color-surface)] rounded-lg p-4">
                                <h5 className="font-semibold text-gray-900 mb-2 font-['Times_New_Roman']">{category.categoryName}</h5>
                                <div className="space-y-1 text-sm">
                                  <p className="text-gray-600 font-['Times_New_Roman']">Products: {category.productCount}</p>
                                  <p className="text-gray-600 font-['Times_New_Roman']">Total Stock: {category.totalStock}</p>
                                  <p className="text-gray-600 font-['Times_New_Roman']">Value: {currencySymbol}{category.totalValue.toLocaleString()}</p>
                                  <p className="text-yellow-600 font-['Times_New_Roman']">Low Stock: {category.lowStockCount}</p>
                                  <p className="text-red-600 font-['Times_New_Roman']">Out of Stock: {category.outOfStockCount}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Filters */}
                      <div className="bg-[var(--color-surface)] rounded-lg p-6 mb-6">
                        <h4 className="text-lg font-semibold text-gray-900 mb-4 font-['Times_New_Roman']">Filters</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                          {/* Category Filter */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2 font-['Times_New_Roman']">Category</label>
                            <select
                              value={selectedCategory}
                              onChange={(e) => setSelectedCategory(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-['Times_New_Roman']"
                            >
                              <option value="all">All Categories</option>
                              {categories.map((category) => (
                                <option key={category._id} value={category._id}>{category.name}</option>
                              ))}
                            </select>
                          </div>

                          {/* Sub Category Filter */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2 font-['Times_New_Roman']">Sub Category</label>
                            <select
                              value={selectedSubCategory}
                              onChange={(e) => setSelectedSubCategory(e.target.value)}
                              disabled={selectedCategory === 'all' || subCategories.length === 0}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-['Times_New_Roman'] disabled:bg-gray-100 disabled:cursor-not-allowed"
                            >
                              <option value="all">All Sub Categories</option>
                              {subCategories.map((subCategory) => (
                                <option key={subCategory._id} value={subCategory._id}>{subCategory.name}</option>
                              ))}
                            </select>
                          </div>

                          {/* Location Filter */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2 font-['Times_New_Roman']">Location</label>
                            <select
                              value={selectedLocation}
                              onChange={(e) => setSelectedLocation(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-['Times_New_Roman']"
                            >
                              <option value="all">All Locations</option>
                              {locations.map((location) => (
                                <option key={location._id} value={location._id}>{location.name}</option>
                              ))}
                            </select>
                          </div>

                          {/* Brand Filter */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2 font-['Times_New_Roman']">Brand</label>
                            <select
                              value={selectedBrand}
                              onChange={(e) => setSelectedBrand(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-['Times_New_Roman']"
                            >
                              <option value="all">All Brands</option>
                              {brands.map((brand) => (
                                <option key={brand._id} value={brand._id}>{brand.name}</option>
                              ))}
                            </select>
                          </div>

                          {/* Status Filter */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2 font-['Times_New_Roman']">Status</label>
                            <select
                              value={selectedStatus}
                              onChange={(e) => setSelectedStatus(e.target.value)}
                              className="w-full px-3 py-2 border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] font-['Times_New_Roman']"
                            >
                              <option value="all">All Status</option>
                              <option value="In Stock">In Stock</option>
                              <option value="Low Stock">Low Stock</option>
                              <option value="Out of Stock">Out of Stock</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      {/* Products Table */}
                      <div className="bg-[var(--color-surface)] rounded-lg shadow overflow-hidden border border-[var(--color-border)]">
                        <div className="px-6 py-4 border-b border-[var(--color-border)]">
                          <h4 className="text-lg font-semibold text-[var(--color-text)] font-['Times_New_Roman']">Product Details</h4>
                        </div>
                        <DoubleScrollbar>
                          <table className="min-w-full divide-y divide-[var(--color-border)] table-fixed">
                            <thead className="bg-[var(--color-background)]">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman'] w-[16rem]">Product</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman'] w-[8rem]">Model</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman'] w-[5rem]">Brand</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman'] w-[5rem]">Category</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman'] w-[5rem]">Sub-category</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman'] w-[4rem]">Location</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Unit</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman'] w-[4rem]">Current Stock</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Status</th>
                              </tr>
                            </thead>
                            <tbody className="bg-[var(--color-surface)] divide-y divide-[var(--color-border)]">
                              {productStockData.products.map((product) => (
                                <tr key={product._id} className="hover:bg-[var(--color-surface-hover)]">
                                  <td className="px-6 py-4 whitespace-normal break-words break-all align-top max-w-[16rem]">
                                    <div>
                                      <div className="text-sm font-medium text-[var(--color-text)] font-['Times_New_Roman']">{product.name}</div>
                                      <div className="text-sm text-[var(--color-text-secondary)] font-['Times_New_Roman']">{product.description}</div>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-normal break-words break-all align-top max-w-[8rem]">
                                    {product.model !== 'N/A' ? (
                                      <a
                                        href={`/products?search=${encodeURIComponent(product.model)}`}
                                        className="text-[var(--color-primary)] hover:underline font-['Times_New_Roman']"
                                      >
                                        {product.model}
                                      </a>
                                    ) : (
                                      <span className="text-[var(--color-text-secondary)] font-['Times_New_Roman']">-</span>
                                    )}
                                  </td>
                                  <td className="px-6 py-4 whitespace-normal break-words break-all align-top max-w-[5rem] text-sm text-[var(--color-text)] font-['Times_New_Roman']">{product.brand || '-'}</td>
                                  <td className="px-6 py-4 whitespace-normal break-words break-all align-top max-w-[5rem] text-sm text-[var(--color-text)] font-['Times_New_Roman']">{product.parentCategory || '-'}</td>
                                  <td className="px-6 py-4 whitespace-normal break-words break-all align-top max-w-[5rem] text-sm text-[var(--color-text)] font-['Times_New_Roman']">{product.subCategory || '-'}</td>
                                  <td className="px-6 py-4 whitespace-normal break-words break-all align-top max-w-[4rem] text-sm text-[var(--color-text)] font-['Times_New_Roman']">{product.location}</td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-[var(--color-text)] font-['Times_New_Roman']">{product.unit}</td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-[var(--color-text)] font-['Times_New_Roman'] text-center max-w-[4rem]">{product.currentStock}</td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStockStatusColor(product.status)} font-['Times_New_Roman']`}>
                                      {product.status}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </DoubleScrollbar>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-12">
                      <AlertCircle className="w-16 h-16 text-[var(--color-text-secondary)] mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-[var(--color-text)] mb-2 font-['Times_New_Roman']">
                        No product stock data available
                      </h3>
                      <p className="text-[var(--color-text-secondary)] font-['Times_New_Roman']">
                        Try refreshing the page or check your connection
                      </p>
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* Supplier Product Purchase Report */}
            {selectedReport === 'supplier-purchase' && (
              <motion.div
                ref={supplierPurchaseReportRef}
                initial={{ opacity: 0 } as any}
                animate={{ opacity: 1 } as any}
                className="space-y-6"
              >
                <div className="flex items-center gap-4 mb-4">
                  <label className="font-['Times_New_Roman']">Supplier</label>
                  <select
                    className="border rounded px-3 py-2 font-['Times_New_Roman']"
                    value={selectedSupplier || ''}
                    onChange={e => setSelectedSupplier(e.target.value)}
                  >
                    <option value="">All Suppliers</option>
                    {suppliers.map((sup: any) => (
                      <option key={sup._id} value={sup._id}>{sup.name}</option>
                    ))}
                  </select>
                  <Button variant="secondary" onClick={() => setSelectedSupplier('')}>
                    Reset
                  </Button>
                  <Button variant="primary" onClick={() => handlePrintOverlay('supplier-purchase', { allSupplierProducts })}>
                    <span className="mr-2">Print</span>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 9V2h12v7M6 18H4a2 2 0 01-2-2V9a2 2 0 012-2h16a2 2 0 012 2v7a2 2 0 01-2 2h-2m-6 0v4m0 0h4m-4 0H8" /></svg>
                  </Button>
                </div>
                <div className="bg-[var(--color-surface)] rounded-lg shadow overflow-hidden border border-[var(--color-border)]">
                  <DoubleScrollbar>
                    <table className="min-w-full divide-y divide-[var(--color-border)]">
                      <thead className="bg-[var(--color-background)]">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Product Name</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Unit</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Total Quantity Purchased</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Details</th>
                        </tr>
                      </thead>
                      <tbody className="bg-[var(--color-surface)] divide-y divide-[var(--color-border)] text-[var(--color-text)]">
                        {filteredSupplierProducts.length === 0 ? (
                          <tr>
                            <td colSpan={4} className="text-center py-8 text-[var(--color-text-secondary)] font-['Times_New_Roman']">No purchase data available for the selected supplier or period.</td>
                          </tr>
                        ) : (
                          filteredSupplierProducts
                            .map((row, idx) => (
                              <tr key={idx} className="hover:bg-[var(--color-background-hover)]">
                                <td className="px-6 py-4 whitespace-nowrap">{row.productName || '-'}</td>
                                <td className="px-6 py-4 whitespace-nowrap">{row.unit || '-'}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-center">{row.qty ?? 0}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-center">
                                  <Button variant="primary" onClick={() => {
                                    setSupplierProductDetailsModalData({
                                      productName: row.productName,
                                      entries: row.details,
                                      supplierName: row.supplierName,
                                    });
                                    setSupplierProductDetailsModalOpen(true);
                                  }}>
                                    Details
                                  </Button>
                                </td>
                              </tr>
                            ))
                        )}
                      </tbody>
                    </table>
                  </DoubleScrollbar>
                </div>
                {/* Details Modal remains unchanged */}
              </motion.div>
            )}

            {/* Supplier Purchase Invoice Report */}
            {selectedReport === 'supplier-invoice' && (
              <motion.div
                ref={supplierInvoiceReportRef}
                initial={{ opacity: 0 } as any}
                animate={{ opacity: 1 } as any}
                className="space-y-6"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <label className="font-['Times_New_Roman'] text-[var(--color-text)]">Supplier</label>
                    <select
                      className="px-3 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] bg-[var(--color-surface)] text-[var(--color-text)] min-w-[180px]"
                      value={selectedSupplierInvoice}
                      onChange={e => setSelectedSupplierInvoice(e.target.value)}
                    >
                      <option value="">All Suppliers</option>
                      {suppliers.map((s: any) => (
                        <option key={s._id} value={s._id}>{s.name}</option>
                      ))}
                    </select>
                    <Button variant="secondary" onClick={() => setSelectedSupplierInvoice('')}>
                      Reset
                    </Button>
                  </div>
                  <Button variant="primary" onClick={() => handlePrintOverlay('supplier-invoice', { invoices: filteredSupplierInvoices })}>
                    <span className="mr-2">Print</span>
                    <Printer className="w-4 h-4" />
                  </Button>
                </div>
                <div ref={supplierInvoiceReportRef}>
                  {supplierReportLoading ? (
                    <div className="flex items-center justify-center h-64"><LoadingSpinner size="lg" /></div>
                  ) : filteredSupplierInvoices.length === 0 ? (
                    <div className="text-center py-8 text-[var(--color-text-secondary)] font-['Times_New_Roman']">No purchase invoices found for the selected supplier or period.</div>
                  ) : (
                    <div className="mb-8">
                      {filteredSupplierInvoices.sort((a: any, b: any) => (a.invoiceNumber || '').localeCompare(b.invoiceNumber || '')).map((inv: any, invIdx: number) => (
                        <div key={inv._id || invIdx} className="mb-6 border border-[var(--color-border)] rounded-lg shadow-sm bg-[var(--color-surface)] text-[var(--color-text)]">
                          <div className="px-6 py-4 border-b border-[var(--color-border)] flex flex-col md:flex-row md:items-center md:justify-between">
                            <div className="space-y-1">
                              <div className="font-semibold text-lg text-blue-600 dark:text-blue-400 font-['Times_New_Roman']">Invoice #: {inv.invoiceNumber}</div>
                              <div className="text-sm text-[var(--color-text-secondary)] font-['Times_New_Roman']">Date: {formatDateTime(inv.createdAt, settings)}</div>
                              <div className="text-sm text-[var(--color-text-secondary)] font-['Times_New_Roman']">Supplier: <span className="text-[var(--color-text)]" style={{ fontWeight: 600 } as unknown as React.CSSProperties}>{inv.customer?.name || inv.customerName || inv.supplier?.name || inv.supplierName || '-'}</span></div>
                            </div>
                            <div className="space-y-1 mt-2 md:mt-0 md:text-right">
                              <div className="text-sm text-[var(--color-text-secondary)] font-['Times_New_Roman']">Total: <span className="text-[var(--color-text)] font-bold" style={{ fontWeight: 600 } as unknown as React.CSSProperties}>{currencySymbol}{(inv.total || 0).toLocaleString()}</span></div>
                            </div>
                          </div>
                          <DoubleScrollbar>
                            <table className="min-w-full divide-y divide-[var(--color-border)]">
                              <thead className="bg-[var(--color-background)]">
                                <tr>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman'] w-80">Product Name</th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman'] w-32">Unit</th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman'] w-28">Qty</th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman'] w-32">Rate</th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman'] w-32">Amount</th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman'] w-32">Discount</th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman'] w-32">Total</th>
                                </tr>
                              </thead>
                              <tbody className="bg-[var(--color-surface)] divide-y divide-[var(--color-border)] text-[var(--color-text)]">
                                {inv.items?.map((item: any, iIdx: number) => (
                                  <tr key={iIdx} className="hover:bg-[var(--color-background-hover)]">
                                    <td className="px-4 py-2 font-['Times_New_Roman']">{item.name || item.product?.name || '-'}</td>
                                    <td className="px-4 py-2 font-['Times_New_Roman']">{item.unit || item.product?.unit || '-'}</td>
                                    <td className="px-4 py-2 font-['Times_New_Roman']">{item.qty ?? item.quantity ?? 0}</td>
                                    <td className="px-4 py-2 font-['Times_New_Roman']">{item.unitPrice ?? item.purchaseRate ?? 0}</td>
                                    <td className="px-4 py-2 font-['Times_New_Roman']">{item.amount ?? (item.qty * item.unitPrice)}</td>
                                    <td className="px-4 py-2 font-['Times_New_Roman']">{item.discount ?? 0}</td>
                                    <td className="px-4 py-2 font-['Times_New_Roman']">{item.total ?? (item.amount - (item.discount || 0))}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </DoubleScrollbar>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* Project-wise Delivery Invoice Report */}
            {selectedReport === 'project-invoice' && (
              <motion.div
                ref={projectInvoiceReportRef}
                initial={{ opacity: 0 } as any}
                animate={{ opacity: 1 } as any}
                className="space-y-6"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <label className="font-['Times_New_Roman'] text-[var(--color-text)]">Customer</label>
                    <select
                      className="px-3 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] bg-[var(--color-surface)] text-[var(--color-text)] min-w-[180px]"
                      value={selectedCustomer}
                      onChange={e => {
                        setSelectedCustomer(e.target.value);
                        setSelectedProject('');
                      }}
                    >
                      <option value="">All Customers</option>
                      {customers.map((c: any) => (
                        <option key={c._id} value={c._id}>{c.name}</option>
                      ))}
                    </select>
                    <Button variant="secondary" onClick={() => { setSelectedCustomer(''); setSelectedProject(''); }}>
                      Reset
                    </Button>
                    {selectedCustomer && (
                      <>
                        <label className="ml-6 font-['Times_New_Roman'] text-[var(--color-text)]">Project</label>
                        <select
                          className="px-3 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] bg-[var(--color-surface)] text-[var(--color-text)] min-w-[180px]"
                          value={selectedProject}
                          onChange={e => setSelectedProject(e.target.value)}
                        >
                          <option value="">All Projects</option>
                          {projects.filter((p: any) => {
                            const customer = customers.find((c: any) => c._id === selectedCustomer);
                            return p.customerName === customer?.name;
                          }).map((p: any) => (
                            <option key={p._id} value={p._id}>{p.name}</option>
                          ))}
                        </select>
                        <Button variant="secondary" onClick={() => setSelectedProject('')}>
                          Reset
                        </Button>
                      </>
                    )}
                  </div>
                  <Button variant="primary" onClick={() => handlePrintOverlay('project-invoice', { filteredProjectList })}>
                    <span className="mr-2">Print</span>
                    <Printer className="w-4 h-4" />
                  </Button>
                </div>
                {/* Table of Delivery Invoices */}
                <div className="bg-[var(--color-surface)] rounded-lg shadow overflow-hidden border border-[var(--color-border)]">
                  <div className="px-6 py-4 border-b border-[var(--color-border)]">
                    <h4 className="text-lg font-semibold text-[var(--color-text)] font-['Times_New_Roman']">Delivery Invoices</h4>
                  </div>
                  <DoubleScrollbar>
                    <table className="min-w-full divide-y divide-[var(--color-border)]">
                      <thead className="bg-[var(--color-background)]">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Invoice Number</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Date</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Customer</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Project</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Total ({currencySymbol})</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Items</th>
                        </tr>
                      </thead>
                      <tbody className="bg-[var(--color-surface)] divide-y divide-[var(--color-border)] text-[var(--color-text)]">
                        {filteredProjectList.length === 0 ? (
                          <tr><td colSpan={6} className="text-center py-8 text-[var(--color-text-secondary)] font-['Times_New_Roman']">No delivery invoices found for the selected filters or period.</td></tr>
                        ) : (
                          filteredProjectList.map((inv: any, idx: number) => {
                            const project = projects.find((p: any) => p._id === (inv.projectId || inv.project?._id));
                            return (
                              <tr key={inv._id || idx} className="hover:bg-[var(--color-background-hover)]">
                                <td className="px-4 py-2 font-['Times_New_Roman']">{inv.invoiceNumber}</td>
                                <td className="px-4 py-2 font-['Times_New_Roman']">{formatDateTime(inv.createdAt, settings)}</td>
                                <td className="px-4 py-2 font-['Times_New_Roman']">{typeof inv.customer === 'object' ? inv.customer?.name || '-' : inv.customer || '-'}</td>
                                <td className="px-4 py-2 font-['Times_New_Roman']">{project?.name || inv.project?.name || '-'}</td>
                                <td className="px-4 py-2 font-['Times_New_Roman']">{currencySymbol}{inv.total?.toLocaleString?.() ?? 0}</td>
                                <td className="px-4 py-2 font-['Times_New_Roman']">
                                  <table className="min-w-full divide-y divide-[var(--color-border)] bg-[var(--color-background)] rounded">
                                    <thead>
                                      <tr>
                                        <th className="px-2 py-1 text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Product</th>
                                        <th className="px-2 py-1 text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Unit</th>
                                        <th className="px-2 py-1 text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Delivered Qty</th>
                                        <th className="px-2 py-1 text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Returned Qty</th>
                                        <th className="px-2 py-1 text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Actual Qty</th>
                                        <th className="px-2 py-1 text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Rate</th>
                                        <th className="px-2 py-1 text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Discount</th>
                                        <th className="px-2 py-1 text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Actual Amount</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-[var(--color-border)] text-[var(--color-text)]">
                                      {(inv.items || []).map((item: any, itemIdx: number) => {
                                        const deliveredQty = item.qty ?? item.quantity ?? 0;
                                        const productId = item.product?._id || (item as any).productId;
                                        const returnedQty = getReturnedQuantity(inv._id, productId);
                                        const actualQty = Math.max(0, deliveredQty - returnedQty);
                                        const rate = item.unitPrice ?? item.saleRate ?? 0;
                                        const discount = item.discount ?? 0;
                                        const actualAmount = Math.max(0, (actualQty * rate) - discount);
                                        
                                        return (
                                          <tr key={itemIdx}>
                                            <td className="px-2 py-1 font-['Times_New_Roman']">{item.name || item.product?.name || '-'}</td>
                                            <td className="px-2 py-1 font-['Times_New_Roman']">{item.unit || item.product?.unit || '-'}</td>
                                            <td className="px-2 py-1 font-['Times_New_Roman']">{deliveredQty}</td>
                                            <td className="px-2 py-1 font-['Times_New_Roman'] text-red-600 dark:text-red-400">{returnedQty}</td>
                                            <td className="px-2 py-1 font-['Times_New_Roman'] font-semibold text-green-600 dark:text-green-400">{actualQty}</td>
                                            <td className="px-2 py-1 font-['Times_New_Roman']">{rate}</td>
                                            <td className="px-2 py-1 font-['Times_New_Roman']">{discount}</td>
                                            <td className="px-2 py-1 font-['Times_New_Roman'] font-semibold text-blue-600 dark:text-blue-400">{currencySymbol}{actualAmount.toLocaleString()}</td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </DoubleScrollbar>
                </div>
              </motion.div>
            )}

            {/* Project-wise Product Delivery Report */}
            {selectedReport === 'project-product' && (
              <motion.div
                ref={projectProductReportRef}
                initial={{ opacity: 0 } as any}
                animate={{ opacity: 1 } as any}
                className="space-y-6"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <label className="font-['Times_New_Roman']">Customer</label>
                    <select
                      className="px-3 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] bg-[var(--color-surface)] text-[var(--color-text)] min-w-[180px]"
                      value={selectedCustomer}
                      onChange={e => {
                        setSelectedCustomer(e.target.value);
                        setSelectedProject('');
                      }}
                    >
                      <option value="">All Customers</option>
                      {customers.map((c: any) => (
                        <option key={c._id} value={c._id}>{c.name}</option>
                      ))}
                    </select>
                    <Button variant="secondary" onClick={() => { setSelectedCustomer(''); setSelectedProject(''); }}>
                      Reset
                    </Button>
                    {selectedCustomer && (
                      <>
                        <label className="ml-6 font-['Times_New_Roman']">Project</label>
                        <select
                          className="px-3 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] bg-[var(--color-surface)] text-[var(--color-text)] min-w-[180px]"
                          value={selectedProject}
                          onChange={e => setSelectedProject(e.target.value)}
                        >
                          <option value="">All Projects</option>
                          {projects.filter((p: any) => {
                            const customer = customers.find((c: any) => c._id === selectedCustomer);
                            return p.customerName === customer?.name;
                          }).map((p: any) => (
                            <option key={p._id} value={p._id}>{p.name}</option>
                          ))}
                        </select>
                        <Button variant="secondary" onClick={() => setSelectedProject('')}>
                          Reset
                        </Button>
                      </>
                    )}
                  </div>
                  <Button variant="primary" onClick={() => handlePrintOverlay('project-product', { products: productList })}>
                    <span className="mr-2">Print</span>
                    <Printer className="w-4 h-4" />
                  </Button>
                </div>
                {/* Table of Delivered Products */}
                <div className="bg-[var(--color-surface)] rounded-lg shadow overflow-hidden border border-[var(--color-border)]">
                  <div className="px-6 py-4 border-b border-[var(--color-border)]">
                    <h4 className="text-lg font-semibold text-[var(--color-text)] font-['Times_New_Roman']">Delivered Products</h4>
                  </div>
                  <DoubleScrollbar>
                    <table className="min-w-full divide-y divide-[var(--color-border)]">
                      <thead className="bg-[var(--color-background)]">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Product</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Unit</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Total Delivered Qty</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Total Returned Qty</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Actual Qty</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Actual Sales ({currencySymbol})</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Details</th>
                        </tr>
                      </thead>
                      <tbody className="bg-[var(--color-surface)] divide-y divide-[var(--color-border)] text-[var(--color-text)]">
                        {productList.length === 0 ? (
                          <tr><td colSpan={7} className="text-center py-8 text-[var(--color-text-secondary)] font-['Times_New_Roman']">No delivered products found for the selected filters or period.</td></tr>
                        ) : (
                          productList.map((prod: any, idx: number) => {
                            // Calculate returned quantities for this product across all delivery invoices
                            let totalReturnedQty = 0;
                            let totalActualAmount = 0;
                            
                            // Get all delivery invoices that contain this product
                            const deliveryInvoicesWithProduct = deliveryInvoices.filter((inv: any) => 
                              inv.items?.some((item: any) => 
                                (item.product?._id === prod.productId || item.name === prod.productName)
                              )
                            );
                            
                            deliveryInvoicesWithProduct.forEach((inv: any) => {
                              inv.items?.forEach((item: any) => {
                                const itemProductId = item.product?._id || (item as any).productId;
                                const itemProductName = item.name || item.product?.name;
                                if (itemProductId === prod.productId || itemProductName === prod.productName) {
                                  const returnedQty = getReturnedQuantity(inv._id, itemProductId);
                                  const deliveredQty = item.qty ?? item.quantity ?? 0;
                                  const actualQty = Math.max(0, deliveredQty - returnedQty);
                                  const rate = item.unitPrice ?? item.saleRate ?? 0;
                                  const discount = item.discount ?? 0;
                                  const actualAmount = Math.max(0, (actualQty * rate) - discount);
                                  
                                  totalReturnedQty += returnedQty;
                                  totalActualAmount += actualAmount;
                                }
                              });
                            });
                            
                            const totalDeliveredQty = prod.qty || 0;
                            const actualQty = Math.max(0, totalDeliveredQty - totalReturnedQty);
                            
                            return (
                              <tr key={prod.productName || idx} className="hover:bg-[var(--color-background-hover)]">
                                <td className="px-4 py-2 font-['Times_New_Roman']">{prod.productName}</td>
                                <td className="px-4 py-2 font-['Times_New_Roman']">{prod.unit}</td>
                                <td className="px-4 py-2 font-['Times_New_Roman']">{totalDeliveredQty}</td>
                                <td className="px-4 py-2 font-['Times_New_Roman'] text-red-600 dark:text-red-400">{totalReturnedQty}</td>
                                <td className="px-4 py-2 font-['Times_New_Roman'] font-semibold text-green-600 dark:text-green-400">{actualQty}</td>
                                <td className="px-4 py-2 font-['Times_New_Roman'] font-semibold text-blue-600 dark:text-blue-400">{currencySymbol}{totalActualAmount.toLocaleString()}</td>
                              <td className="px-4 py-2 font-['Times_New_Roman']">
                                <Button variant="primary" onClick={() => {
                                  setDetailsModalData({
                                    productName: prod.productName,
                                    entries: prod.details,
                                    projectName: prod.details[0]?.project || '',
                                  });
                                  setDetailsModalOpen(true);
                                }}>
                                  Details
                                </Button>
                              </td>
                            </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </DoubleScrollbar>
                </div>
                {/* Details Modal for Product Invoices */}
                <Modal isOpen={detailsModalOpen} onClose={() => setDetailsModalOpen(false)} size="xl" className="!max-w-7xl">
                  <div className="flex justify-end p-2">
                    <Button variant="primary" onClick={() => handlePrintOverlay('details-modal', detailsModalData)}>
                      Print
                    </Button>
                  </div>
                  <div ref={detailsModalPrintRef}>
                    {detailsModalData && (
                      <div className="p-0 text-[var(--color-text)]">
                        <div className="px-8 pt-8 pb-4">
                          <h3 className="text-2xl font-bold mb-6 text-center font-['Times_New_Roman']">
                            {detailsModalData.productName} - {detailsModalData.entries[0]?.customer || '-'} / {detailsModalData.projectName || '-'} Details
                          </h3>
                        </div>
                        <DoubleScrollbar className="px-8 pb-8">
                          <table className="min-w-full divide-y divide-[var(--color-border)]">
                            <thead>
                              <tr className="bg-[var(--color-background)]">
                                <th className="px-4 py-3 text-left text-sm font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Invoice #</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Date</th>
                                <th className="px-4 py-3 text-right text-sm font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Qty</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Unit</th>
                                <th className="px-4 py-3 text-right text-sm font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Rate</th>
                                <th className="px-4 py-3 text-right text-sm font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Amount</th>
                                <th className="px-4 py-3 text-right text-sm font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Discount</th>
                                <th className="px-4 py-3 text-right text-sm font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Total</th>
                              </tr>
                            </thead>
                            <tbody className="bg-[var(--color-surface)] divide-y divide-[var(--color-border)]">
                              {detailsModalData.entries.map((entry: any, idx: number) => (
                                <tr key={idx} className="hover:bg-[var(--color-background-hover)]">
                                  <td className="px-4 py-2 font-['Times_New_Roman']">
                                    {entry._id ? (
                                      <Button variant="primary" onClick={async (e) => {
                                        e.preventDefault();
                                        setDeliveryInvoiceLoading(true);
                                        setShowDeliveryInvoiceModal(true);
                                        try {
                                          const response = await apiService.get(`/invoices/by-number/${entry.invoiceNumber}`);
                                          console.log('Fetched invoice:', response.data);
                                          if (response.data && response.data.invoice) {
                                            setDeliveryInvoiceData(response.data.invoice);
                                          } else {
                                            setDeliveryInvoiceData(response.data);
                                          }
                                        } catch (err) {
                                          setDeliveryInvoiceData(null);
                                          toast.error('Failed to fetch invoice');
                                        }
                                        setDeliveryInvoiceLoading(false);
                                      }}>
                                        {entry.invoiceNumber || '-'}
                                      </Button>
                                    ) : (
                                      entry.invoiceNumber || '-'
                                    )}
                                  </td>
                                  <td className="px-4 py-2 font-['Times_New_Roman']">{formatDateTime(entry.date, settings)}</td>
                                  <td className="px-4 py-2 font-['Times_New_Roman'] text-right">{entry.qty ?? entry.quantity ?? 0}</td>
                                  <td className="px-4 py-2 font-['Times_New_Roman']">{entry.unit || entry.product?.unit || '-'}</td>
                                  <td className="px-4 py-2 font-['Times_New_Roman'] text-right">{entry.unitPrice ?? entry.saleRate ?? 0}</td>
                                  <td className="px-4 py-2 font-['Times_New_Roman'] text-right">
                                    {typeof entry.amount !== 'undefined' && entry.amount !== null && entry.amount !== '-' 
                                      ? entry.amount 
                                      : (() => {
                                          const qty = parseFloat(entry.qty ?? entry.quantity ?? 0);
                                          const rate = parseFloat(entry.unitPrice ?? entry.saleRate ?? 0);
                                          if (!isNaN(qty) && !isNaN(rate)) {
                                            const calcAmount = qty * rate;
                                            return isNaN(calcAmount) ? 0 : calcAmount;
                                          }
                                          return 0;
                                        })()
                                    }
                                  </td>
                                  <td className="px-4 py-2 font-['Times_New_Roman'] text-right">{entry.discount ?? 0}</td>
                                  <td className="px-4 py-2 font-['Times_New_Roman'] text-right">{typeof entry.total !== 'undefined' && entry.total !== null && entry.total !== '-' ? entry.total : (() => { const qty = parseFloat(entry.qty ?? entry.quantity ?? 0); const rate = parseFloat(entry.unitPrice ?? entry.saleRate ?? 0); const discount = parseFloat(entry.discount ?? 0); if (!isNaN(qty) && !isNaN(rate)) { const calcTotal = qty * rate - discount; return isNaN(calcTotal) ? 0 : calcTotal.toLocaleString(); } return 0; })()}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </DoubleScrollbar>
                      </div>
                    )}
                  </div>
                </Modal>
              </motion.div>
            )}
          </div>
        </div>
      </div>
      {/* Print Modal */}
      <PrintModal 
        isOpen={printModalOpen} 
        onClose={() => setPrintModalOpen(false)} 
        title={`Print ${printModalData?.type ? printModalData.type.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Report'}`}
      >
        {printModalData && (
          <>
            {printModalData.type === 'sales' && <SalesReportPrintView data={printModalData.data} currencySymbol={currencySymbol} />}
            {printModalData.type === 'inventory' && <InventoryReportPrintView data={printModalData.data} currencySymbol={currencySymbol} />}
            {printModalData.type === 'product-stock' && <ProductStockReportPrintView data={printModalData.data} currencySymbol={currencySymbol} />}
            {printModalData.type === 'supplier-purchase' && <SupplierProductPurchaseReportPrintView data={printModalData.data} currencySymbol={currencySymbol} />}
            {printModalData.type === 'supplier-invoice' && (
              <>
                {printModalData.data.invoices && printModalData.data.invoices.length > 0 ? (
                  printModalData.data.invoices.sort((a: any, b: any) => (a.invoiceNumber || '').localeCompare(b.invoiceNumber || '')).map((inv: any, invIdx: number) => (
                    <div key={inv._id || invIdx} style={{ marginBottom: 32, border: '1px solid #eee', borderRadius: 8, background: '#fff', boxShadow: '0 1px 4px #eee' }}>
                      <div style={{ padding: '16px 24px', borderBottom: '1px solid #eee', display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <div style={{ fontWeight: 'bold', fontSize: 16, color: '#2563eb' }}>Invoice #: {inv.invoiceNumber}</div>
                        <div style={{ fontSize: 14, color: '#444' }}>Date: {formatDateTime(inv.createdAt, settings)}</div>
                        <div style={{ fontSize: 14, color: '#444' }}>Supplier: <span style={{ fontWeight: 600 }}>{inv.customer?.name || inv.customerName || inv.supplier?.name || inv.supplierName || '-'}</span></div>
                      </div>
                      <div style={{ overflowX: 'auto', padding: '0 24px 16px 24px' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8, fontSize: 14 }}>
                          <thead>
                            <tr style={{ background: '#f3f4f6' }}>
                              <th style={{ border: '1px solid #ccc', padding: 8 }}>Product Name</th>
                              <th style={{ border: '1px solid #ccc', padding: 8 }}>Unit</th>
                              <th style={{ border: '1px solid #ccc', padding: 8 }}>Qty</th>
                              <th style={{ border: '1px solid #ccc', padding: 8 }}>Rate</th>
                              <th style={{ border: '1px solid #ccc', padding: 8 }}>Amount</th>
                              <th style={{ border: '1px solid #ccc', padding: 8 }}>Discount</th>
                              <th style={{ border: '1px solid #ccc', padding: 8 }}>Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(inv.items || []).map((item: any, itemIdx: number) => (
                              <tr key={itemIdx}>
                                <td style={{ border: '1px solid #ccc', padding: 8 }}>{item.name || item.product?.name || '-'}</td>
                                <td style={{ border: '1px solid #ccc', padding: 8 }}>{item.unit || item.product?.unit || '-'}</td>
                                <td style={{ border: '1px solid #ccc', padding: 8, textAlign: 'right' }}>{item.qty ?? item.quantity ?? 0}</td>
                                <td style={{ border: '1px solid #ccc', padding: 8, textAlign: 'right' }}>{item.purchaseRate ?? item.unitPrice ?? 0}</td>
                                <td style={{ border: '1px solid #ccc', padding: 8, textAlign: 'right' }}>
                                  {typeof item.amount !== 'undefined' && item.amount !== null && item.amount !== '-' 
                                    ? item.amount 
                                    : (() => {
                                        const qty = parseFloat(item.qty ?? item.quantity ?? 0);
                                        const rate = parseFloat(item.purchaseRate ?? item.unitPrice ?? 0);
                                        if (!isNaN(qty) && !isNaN(rate)) {
                                          const calcAmount = qty * rate;
                                          return isNaN(calcAmount) ? 0 : calcAmount;
                                        }
                                        return 0;
                                      })()
                                  }
                                </td>
                                <td style={{ border: '1px solid #ccc', padding: 8 }}>{item.discount ?? 0}</td>
                                <td style={{ border: '1px solid #ccc', padding: 8 }}>
                                  {typeof item.total !== 'undefined' && item.total !== null && item.total !== '-' ?
                                    item.total :
                                    (() => {
                                      const qty = parseFloat(item.qty ?? item.quantity ?? 0);
                                      const rate = parseFloat(item.purchaseRate ?? item.unitPrice ?? 0);
                                      const discount = parseFloat(item.discount ?? 0);
                                      if (!isNaN(qty) && !isNaN(rate)) {
                                        const calcTotal = qty * rate - discount;
                                        return isNaN(calcTotal) ? '-' : calcTotal.toLocaleString();
                                      }
                                      return '-';
                                    })()
                                  }
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <div style={{ textAlign: 'right', marginTop: 8, fontWeight: 'bold', fontSize: 16 }}>
                          Total: {currencySymbol}{(inv.total || 0).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={{ color: '#888', fontStyle: 'italic', margin: '16px 0' }}>No purchase invoices found for this supplier or period.</div>
                )}
              </>
            )}
            {printModalData.type === 'project-invoice' && <ProjectInvoiceReportScreenLikePrintView data={printModalData.data} customers={customers} projects={projects} currencySymbol={currencySymbol} />}
            {printModalData.type === 'project-product' && <ProjectProductReportPrintView data={printModalData.data} currencySymbol={currencySymbol} />}
            {printModalData.type === 'details-modal' && printModalData.data && (
              <DetailsModalPrintView data={printModalData.data} currencySymbol={currencySymbol} />
            )}
          </>
        )}
      </PrintModal>
      {/* Render Delivery Invoice Print View Modal at the end of ReportsPage */}
      <Modal
        isOpen={showDeliveryInvoiceModal}
        onClose={() => setShowDeliveryInvoiceModal(false)}
        size="xl"
        className="max-w-4xl"
      >
        {deliveryInvoiceLoading ? (
          <div className="flex items-center justify-center h-64">
            <LoadingSpinner size="lg" />
          </div>
        ) : deliveryInvoiceData ? (
          <DeliveryInvoicePrintView 
            invoice={deliveryInvoiceData}
            settings={settings}
            currentPage={1}
            itemsPerPage={100}
            printMode={false}
          />
        ) : (
          <div className="flex items-center justify-center h-64 text-gray-500">
            No invoice data found.
          </div>
        )}
      </Modal>
      {/* Details Modal for Supplier Product Purchase Report */}
      <Modal isOpen={supplierProductDetailsModalOpen} onClose={() => setSupplierProductDetailsModalOpen(false)} size="xl" className="!max-w-7xl">
        {supplierProductDetailsModalData && (
          <div className="p-0 text-[var(--color-text)]">
            <div className="px-8 pt-8 pb-4">
              <h3 className="text-2xl font-bold mb-6 text-center font-['Times_New_Roman']">
                {supplierProductDetailsModalData.productName} - {supplierProductDetailsModalData.supplierName} Details
              </h3>
            </div>
            <DoubleScrollbar className="px-8 pb-8">
              <table className="min-w-full divide-y divide-[var(--color-border)]">
                <thead>
                  <tr className="bg-[var(--color-background)]">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Invoice #</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Date</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Qty</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Unit</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Rate</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Amount</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Discount</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Total</th>
                  </tr>
                </thead>
                <tbody className="bg-[var(--color-surface)] divide-y divide-[var(--color-border)]">
                  {supplierProductDetailsModalData.entries.map((entry: any, idx: number) => (
                    <tr key={idx} className="hover:bg-[var(--color-background-hover)]">
                      <td className="px-4 py-2 font-['Times_New_Roman']">{entry.invoiceNumber}</td>
                      <td className="px-4 py-2 font-['Times_New_Roman']">{formatDateTime(entry.date, settings)}</td>
                      <td className="px-4 py-2 font-['Times_New_Roman'] text-right">{entry.qty ?? entry.quantity ?? 0}</td>
                      <td className="px-4 py-2 font-['Times_New_Roman']">{entry.unit || entry.product?.unit || '-'}</td>
                      <td className="px-4 py-2 font-['Times_New_Roman'] text-right">{entry.purchaseRate ?? entry.unitPrice ?? 0}</td>
                      <td className="px-4 py-2 font-['Times_New_Roman'] text-right">
                        {typeof entry.amount !== 'undefined' && entry.amount !== null && entry.amount !== '-' 
                          ? entry.amount 
                          : (() => {
                              const qty = parseFloat(entry.qty ?? entry.quantity ?? 0);
                              const rate = parseFloat(entry.purchaseRate ?? entry.unitPrice ?? 0);
                              if (!isNaN(qty) && !isNaN(rate)) {
                                const calcAmount = qty * rate;
                                return isNaN(calcAmount) ? 0 : calcAmount;
                              }
                              return 0;
                            })()
                        }
                      </td>
                      <td className="px-4 py-2 font-['Times_New_Roman'] text-right">{entry.discount ?? 0}</td>
                      <td className="px-4 py-2 font-['Times_New_Roman'] text-right">{typeof entry.total !== 'undefined' && entry.total !== null && entry.total !== '-' ? entry.total : (() => { const qty = parseFloat(entry.qty ?? entry.quantity ?? 0); const rate = parseFloat(entry.purchaseRate ?? entry.unitPrice ?? 0); const discount = parseFloat(entry.discount ?? 0); if (!isNaN(qty) && !isNaN(rate)) { const calcTotal = qty * rate - discount; return isNaN(calcTotal) ? 0 : calcTotal.toLocaleString(); } return 0; })()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </DoubleScrollbar>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ReportsPage;